// Fleet → Assets & onboarding — the workspace, on the Brands Manager paradigm.
//
// Founder, 5 Sep, in three passes: (1) registries not a board; (2) behave
// like the BM campaigns screen; (3) LOOK like it too — shiv/page.js is the
// precedent, not an inspiration. So this screen copies BM's grammar:
//
//   search bar   full width, above the tabs
//   tab CARDS    icon + label + "N selected ×" pill, the active card fused
//                onto the white sheet below it
//   toolbar      real buttons inside the sheet: create (green), Edit
//                (enabled at exactly one selection), Export (selected rows
//                or the whole view), the facet chips
//   table        white, ruled, column headers with help-dots, green
//                selected rows, "Results from N …" footer
//   drawer       row click → right drawer; V/S/M/D/O/T slots are its left
//                rail, each pane IS the alteration mechanism; the action
//                log (real dated history only) is its last page
//
// And BM's selection model: check rows in ANY tab and every OTHER tab
// narrows to what shares a deployment with them — peers INTERSECT through
// the deployment hub (unlike BM's campaign ⊃ ad set ⊃ ad nesting), a tab's
// own selection never hides its own rows. All filtering, faceting and
// pagination happen in SQL before the browser sees a row.

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { NavLink, useSearchParams } from 'react-router-dom';
import {
  Search, Plus, X, ScrollText, Download, Pencil, ImagePlus, CheckCircle2, Copy,
  Boxes, Monitor, CreditCard, Car, Users, Building2, Cpu, Info, FileCheck, History, ChevronDown, Check, Power,
} from 'lucide-react';
import { Loading, ErrorState, Pill } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import s from './Onboarding.module.css';
// Modals portal OUT of the sheet (whose z-index would trap them under the
// tab cards) but INTO .ggv2 — the design tokens are scoped there on purpose,
// and a body-level portal would render unstyled (proven by screenshot).
const modalHost = () => document.querySelector('.ggv2') || document.body;

const inrP = (paise, dp = 0) => (paise == null ? '—'
  : `₹${(Number(paise) / 100).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtWhen = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
  + ' ' + new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }) : '—');
const daysTo = (d) => (d ? Math.floor((new Date(d) - Date.now()) / 86400000) : null);
const relTime = (d) => {
  if (!d) return 'never';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return days < 30 ? `${days}d ago` : fmtDate(d);
};
const LiveDot = ({ status, at }) => {
  const mins = at ? (Date.now() - new Date(at)) / 60000 : Infinity;
  const bg = mins <= 10 ? 'var(--grow)' : mins <= 24 * 60 ? 'var(--warn-text)' : 'var(--ink-faint)';
  return (
    <span title={`heard ${at ? fmtWhen(at) : 'never'} · flag: ${status || 'unknown'}`}
          style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', marginRight: 5, flex: 'none',
                   background: bg }} aria-hidden="true" />
  );
};

const LIFECYCLES = ['onboarding', 'deployed', 'parked', 'retired'];
const LIFECYCLE_TONE = { onboarding: 'pend', deployed: 'grow', parked: 'warn', retired: 'bad' };
const isCab = (r) => r.platform === 'in_cab';

const DIMS = ['deployments', 'screens', 'sims', 'vehicles', 'drivers', 'premise_owners', 'device_owners'];
const EMPTY_SEL = { deployments: [], screens: [], sims: [], vehicles: [], drivers: [], premise_owners: [], device_owners: [] };

const shapedList = (d) => {
  if (!d || typeof d !== 'object' || !Array.isArray(d.rows)) {
    throw new Error('The server did not answer with this registry. If this is a local dev session, restart the iot-admin server (Ctrl+C, then ./scripts/dev-iot-admin.sh).');
  }
  return d;
};

// A tab sends every OTHER dimension's selection as sel_* params — its own
// dimension is left out so selecting rows never hides them from their list.
const selParams = (sel, excludeDim) => {
  const p = {};
  for (const d of DIMS) {
    if (d !== excludeDim && sel[d].length) p[`sel_${d}`] = sel[d].join(',');
  }
  return p;
};
const anySelected = (sel) => DIMS.some((d) => sel[d].length > 0);
const rideLive = (r) => r.deployment_id && r.premise_lifecycle !== 'retired';
const DIM_LABEL = { deployments: 'premises', screens: 'devices', sims: 'SIMs',
  vehicles: 'vehicles', drivers: 'drivers', premise_owners: 'premise owners', device_owners: 'device owners' };

// BM's export: the selected rows, or the whole loaded view when none are.
const csvEsc = (v) => { const t = v == null ? '' : String(v); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
function exportCsv(noun, cols, rows, selIds) {
  const src = selIds.length ? rows.filter((r) => selIds.includes(r.id)) : rows;
  const lines = [cols.map((c) => csvEsc(c[0])).join(',')];
  src.forEach((r) => lines.push(cols.map((c) => csvEsc(c[1](r))).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${noun}-export-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* ── one paginated-list engine for every tab ───────────────────────────── */

function useRegistry(path, dim, sel, q, extra = {}, refresh = 0) {
  const [state, setState] = useState('');
  // snapshot at mount: revisiting a tab pins the rows you had checked to the
  // first fold (server-side), without the list jumping as you keep checking
  const [pin] = useState(() => sel[dim].join(','));
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const limit = 50;
  const selKey = JSON.stringify(selParams(sel, dim));
  const extraKey = JSON.stringify(extra);

  const load = useCallback(async (nextOffset, append) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit, offset: nextOffset,
        ...(q ? { q } : {}), ...(state ? { state } : {}), ...(pin ? { pin } : {}),
        ...JSON.parse(extraKey), ...JSON.parse(selKey),
      };
      const d = shapedList((await api.get(path, { params })).data);
      setRows((prev) => (append ? [...prev, ...d.rows] : d.rows));
      setCounts(d.counts || null);
      setOffset(nextOffset);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [path, q, state, pin, extraKey, selKey]);

  useEffect(() => {
    const t = setTimeout(() => load(0, false), q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, q, refresh]);

  return {
    state, setState, rows, counts, loading, error,
    reload: () => load(0, false),
    more: () => load(offset + limit, true),
    hasMore: counts ? rows.length < Number(counts.total) && rows.length >= limit : false,
  };
}

/* ── selection + table plumbing shared by every tab ────────────────────── */

function Cb({ checked, onChange, label }) {
  return (
    <input type="checkbox" className={s.cb} checked={checked} aria-label={label}
           onClick={(e) => e.stopPropagation()} onChange={onChange} />
  );
}

// Column headers, BM-style: label + a help-dot that explains the column.
function HeaderRow({ gridClass, cols, pageIds, selIds, onToggleAll, style }) {
  const allOn = pageIds.length > 0 && pageIds.every((id) => selIds.includes(id));
  const someOn = pageIds.some((id) => selIds.includes(id));
  return (
    <div className={`${gridClass} ${s.thRow}`} style={style}>
      <input type="checkbox" className={s.cb} checked={allOn}
             ref={(el) => { if (el) el.indeterminate = !allOn && someOn; }}
             aria-label="Select all rows on this page" onChange={onToggleAll} />
      {cols.map(([label, help], i) => (
        <span key={i} className={s.th}>
          {label}
          {help && <span className={s.helpDot} title={help}><Info size={11} aria-label={help} /></span>}
        </span>
      ))}
    </div>
  );
}

// The BM sheet: toolbar (buttons + facet chips) fused under the active tab
// card, then the table, then the results footer.
function Sheet({ reg, noun, toolbar, facets, note, header, footerExtra, children }) {
  return (
    <div className={s.sheet}>
      <div className={s.toolbar}>
        {toolbar}
        <span className={s.spacer} />
        {facets.map(([key, label, count]) => (
          <button key={key || 'all'} type="button"
                  className={`${s.chip} ${reg.state === key ? s.chipOn : ''}`}
                  onClick={() => reg.setState(reg.state === key ? '' : key)}>
            {label}{count != null ? ` · ${count}` : ''}
          </button>
        ))}
      </div>
      {typeof note === 'string' && <div className={s.noteBar}>{note}</div>}
      {note != null && typeof note !== 'string' && <div style={{ padding: '0 var(--s3)' }}>{note}</div>}
      {header}
      {reg.loading && !reg.rows.length && <div style={{ padding: 'var(--s4)' }}><Loading what="the registry" /></div>}
      {reg.error && <div style={{ padding: 'var(--s4)' }}><ErrorState error={reg.error} onRetry={reg.reload} /></div>}
      {!reg.error && children}
      {!reg.loading && !reg.error && !reg.rows.length && (
        <div style={{ padding: 'var(--s4)', color: 'var(--ink-faint)', fontSize: 13 }}>Nothing matches.</div>
      )}
      {reg.hasMore && (
        <button type="button" className={s.loadMore} onClick={reg.more} disabled={reg.loading}>
          {reg.loading ? 'loading…' : `Load more (${reg.rows.length} of ${reg.counts.total})`}
        </button>
      )}
      {reg.counts && (
        <div className={s.resultsFoot}>
          Results from {reg.counts.total} {noun}
        </div>
      )}
      {footerExtra}
    </div>
  );
}

function EditButton({ selIds, onEdit }) {
  return (
    <button type="button" className={s.btnTool} disabled={selIds.length !== 1}
            title={selIds.length === 1 ? 'Open the selected row' : 'Select exactly one row to edit'}
            onClick={() => onEdit(selIds[0])}>
      <Pencil size={13} aria-hidden="true" /> Edit
    </button>
  );
}

function ExportButton({ onClick }) {
  return (
    <button type="button" className={s.btnTool} title="Export the selected rows (or the whole view) as CSV" onClick={onClick}>
      <Download size={13} aria-hidden="true" /> Export
    </button>
  );
}

/* ── the slot model (unchanged semantics) ──────────────────────────────── */

const SLOTS = [
  { key: 'vehicle', Icon: Car, label: 'vehicle', applies: isCab,
    state: (r) => (r.vehicle_id ? (daysTo(r.nearest_doc_expiry) != null && daysTo(r.nearest_doc_expiry) <= 30 ? 'warn' : 'ok') : 'miss'),
    tip: (r) => (r.vehicle_id
      ? `Vehicle ${r.registration_number}${r.nearest_doc_expiry ? ` · nearest document expiry ${fmtDate(r.nearest_doc_expiry)}` : ''}`
      : 'No vehicle linked — open to pick one.') },
  { key: 'screen', Icon: Monitor, label: 'device', applies: () => true,
    // a bridged device that is lost/rma/retired (V343) FILLS the slot but is
    // DEAD — a distinct red state, never mistakable for merely offline
    state: (r) => (r.screen_id ? (deviceOut(r) ? 'out' : 'ok') : 'miss'),
    tip: (r) => (r.screen_id
      ? (deviceOut(r)
        ? `Device ${r.thing_name} is out of service — ${r.device_lifecycle}. Serving is off; swap it or reactivate it on the Devices tab.`
        : `Device ${r.thing_name} · heard ${relTime(r.last_heard_at)}`)
      : 'No device installed — open to install one from the shelf.') },
  { key: 'sim', Icon: CreditCard, label: 'SIM', applies: () => true,
    state: (r) => (r.sim_id ? 'ok' : 'miss'),
    tip: (r) => (r.sim_id ? `SIM ${r.sim_number} · ${inrP(r.monthly_cost_paise)}/month` : 'No SIM allocated — open to allocate from stock.') },
  { key: 'driver', Icon: Users, label: 'driver', applies: isCab,
    // absent = unconfigured (half-moon), but deliberately NOT part of the
    // money verdict: the gate runs on trips and hours, not names
    state: (r) => (r.driver_name ? 'ok' : 'miss'),
    tip: (r) => (r.driver_name ? `Driver ${r.driver_name}` : 'No active driver — unconfigured; advisory for money.') },
  { key: 'operator', Icon: Building2, label: 'operator', applies: () => true,
    // ok = money-plane owner set; house = only a registry tag (earnings stay
    // house money — yellow half-moon); miss = nobody at all (red half-moon)
    state: (r) => (r.drift ? 'drift' : r.money_operator ? 'ok' : r.registry_operator ? 'house' : 'miss'),
    tip: (r) => (r.drift
      ? `DRIFT: registry ${r.registry_operator} ≠ money-plane owner ${r.money_operator}.`
      : r.money_operator ? `Premise owner ${r.money_operator}.`
        : r.registry_operator ? `Tagged ${r.registry_operator} but NO money-plane owner — earnings stay house money.`
          : 'No operator — earnings stay house money.') },
  { key: 'terms', Icon: FileCheck, label: 'terms', applies: () => true,
    // an operator with nothing accepted = unconfigured; no operator at all =
    // nothing to accept yet (grey, not a half-moon)
    state: (r) => ((r.money_operator || r.registry_operator) ? (r.operator_acceptances > 0 ? 'ok' : 'miss') : 'na'),
    tip: (r) => (r.operator_acceptances > 0
      ? (r.has_own_terms ? 'TnC accepted · own terms.' : 'TnC accepted · default terms.')
      : 'No tenant-level TnC on record — advisory.') },
];
const missingOf = (r) => SLOTS.filter((sl) => sl.applies(r) && ['vehicle', 'screen', 'sim', 'operator'].includes(sl.key) && sl.state(r) === 'miss').map((sl) => sl.label);
const deviceOut = (r) => !!r.screen_id && OUT_OF_SERVICE.includes(r.device_lifecycle);

/* ── today's money gate (advisory; server refreshes ~hourly) ────────────────
   The media console's trichotomy, ported: On track | Not yet | NOTHING when
   there is no verdict (no device, stationary premise, no covering
   agreement, or the verdict itself was lost server-side). "Not yet" is
   mute, not bad — at 00:30 IST it is the normal state of a healthy cab.
   Progress prints ONLY the thresholds the gate actually sets (each key is
   nullable — printing an absent one would invent a requirement). */
const fmtGateQty = (n) => { const v = Number(n) || 0; return Number.isInteger(v) ? String(v) : v.toFixed(1); };
const gateParts = (r, vs) => {
  const g = r.today_gate || {};
  const parts = [];
  if (g.min_trips != null) parts.push(`${fmtGateQty(r.today_trips)}${vs ? ` of ${fmtGateQty(g.min_trips)}` : ''} trips`);
  if (g.min_presence_hours != null) parts.push(`${fmtGateQty(r.today_pax_h)}${vs ? ` of ${fmtGateQty(g.min_presence_hours)}` : ''} pax-h`);
  if (g.min_driving_hours != null) parts.push(`${fmtGateQty(r.today_drv_h)}${vs ? ` of ${fmtGateQty(g.min_driving_hours)}` : ''} drv-h`);
  return parts.join(' · ');
};
const gateLine = (r) => (r.on_track
  ? `On track — ${gateParts(r, false) || 'no gate terms set'}`
  : `${gateParts(r, true) || 'no activity yet'} — not yet`);
// the compact verdict word for the worklist row; null renders NOTHING
const gateWord = (r) => (r.on_track == null ? null
  : (
    <span className={s.rowSub} style={r.on_track ? { color: 'var(--grow-text)' } : undefined}
          title={`Today's gate (refreshed hourly): ${gateLine(r)}`}>
      {r.on_track ? 'On track' : 'Not yet'}
    </span>
  ));
const segCls = (st) => (st === 'ok' ? s.segOk : st === 'drift' || st === 'warn' ? s.segWarn : st === 'na' ? s.segDim : s.segMiss); // 'out' rides segMiss (red): a dead device is never mistakable for merely-warned; 'house'/'miss' share it, the moons tell them apart

function Track({ r }) {
  return (
    <span className={s.track}>
      {SLOTS.filter((sl) => sl.applies(r)).map((sl) => (
        <span key={sl.key} className={`${s.seg} ${segCls(sl.state(r))}`} title={sl.tip(r)}
              role="img" aria-label={`${sl.label}: ${sl.tip(r)}`}>
          <sl.Icon size={11} aria-hidden="true" />
        </span>
      ))}
    </span>
  );
}

/* ── Deployments tab: the worklist ─────────────────────────────────────── */

function DeploymentsTab({ sel, toggle, toggleAll, openDrawer, q, refresh, onChanged }) {
  const [needs, setNeeds] = useState('');
  const [sort, setSort] = useState('urgency');
  const [intakeOpen, setIntakeOpen] = useState(false);
  const reg = useRegistry('/v2/onboarding/deployments', 'deployments', sel, q,
    { ...(needs ? { needs } : {}), sort }, refresh);
  const facetChips = [['work', 'needs work', reg.counts?.needs_work],
    ['drift', 'drift', reg.counts?.drift],
    ['', 'everything', reg.counts?.total]];
  return (
    <div className={s.sheet}>
      <div className={s.toolbar}>
        <button type="button" className={s.btnCreate} onClick={() => setIntakeOpen((v) => !v)}>
          <Plus size={14} aria-hidden="true" /> New premise
        </button>
        <EditButton selIds={sel.deployments} onEdit={(id) => openDrawer({ kind: 'deployment', id })} />
        <ExportButton onClick={() => exportCsv('deployments', [
          ['Deployment', (r) => r.node_name], ['Platform', (r) => r.platform], ['City', (r) => r.city],
          ['Operator', (r) => r.operator || ''], ['Screen', (r) => r.thing_name || ''],
          ['SIM', (r) => r.sim_number || ''], ['Vehicle', (r) => r.registration_number || ''],
          ['Verdict', (r) => (r.drift ? 'operator drift' : deviceOut(r) ? 'device out of service' : missingOf(r).length ? `needs ${missingOf(r).join(', ')}` : 'ready')],
          ['Lifecycle', (r) => r.lifecycle_status],
        ], reg.rows, sel.deployments)} />
        <label className={s.hint} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          sort
          <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort premises"
                  style={{ font: 'inherit', fontSize: 12.5, color: 'var(--ink)', background: 'var(--paper)',
                           border: '1px solid var(--line)', borderRadius: 8, padding: '5px 8px' }}>
            <option value="urgency">urgency — drift, earning-but-broken, newest</option>
            <option value="newest">newest premise first</option>
            <option value="seen">last heard</option>
            <option value="name">name</option>
          </select>
        </label>
        <span className={s.spacer} />
        {facetChips.map(([key, label, count]) => (
          <button key={key || 'all'} type="button"
                  className={`${s.chip} ${needs === key ? s.chipOn : ''}`}
                  onClick={() => setNeeds(key)}>
            {label}{count != null ? ` · ${count}` : ''}
          </button>
        ))}
        {reg.counts && <span className={s.hint}>{reg.counts.ready} of {reg.counts.total} ready</span>}
      </div>
      {intakeOpen && <PremiseIntake onDone={onChanged} onClose={() => setIntakeOpen(false)} />}
      <HeaderRow gridClass={s.rowHead}
                 cols={[
                   ['Premise', 'The place a device earns from — born here the day the location is signed; name · premise type · city · premise owner.'],
                   ['Device', 'The device riding this premise, resolved through the hardware bridge at first play. Heard = the freshest exchange — telemetry ≈1.5 min while alive; green ≤10 min, amber ≤24 h.'],
                   ['SIM', 'The open dated allocation from the SIM registry.'],
                   ['Slots', 'One chip per slot — vehicle, device, SIM, driver, operator, terms (same icons as the tabs). Green filled, amber missing, red drift, grey advisory.'],
                   ['Verdict', 'What stands between this deployment and ready. Cabs carry a second word — today\'s money gate, On track / Not yet, refreshed hourly; no word means no verdict (no device, or no covering agreement).'],
                   ['Lifecycle', 'onboarding → deployed → parked → retired'],
                 ]}
                 pageIds={reg.rows.map((r) => r.id)} selIds={sel.deployments}
                 onToggleAll={() => toggleAll('deployments', reg.rows.map((r) => r.id))} />
      {reg.loading && !reg.rows.length && <div style={{ padding: 'var(--s4)' }}><Loading what="the worklist" /></div>}
      {reg.error && <div style={{ padding: 'var(--s4)' }}><ErrorState error={reg.error} onRetry={reg.reload} /></div>}
      {!reg.error && reg.rows.map((r) => {
        const missing = r.drift ? [] : missingOf(r);
        const checked = sel.deployments.includes(r.id);
        return (
          <div key={r.id} className={s.row}>
            <div className={`${s.rowHead} ${checked ? s.rowSel : ''}`} role="button" tabIndex={0}
                 onClick={() => openDrawer({ kind: 'deployment', id: r.id })}
                 onKeyDown={(e) => { if (e.key === 'Enter') openDrawer({ kind: 'deployment', id: r.id }); }}>
              <Cb checked={checked} onChange={() => toggle('deployments', r.id)} label={`Select ${r.node_name}`} />
              <span className={s.rowName}>
                {r.node_name}
                <span className={s.rowSub}>{isCab(r) ? 'in-cab' : r.platform.replace(/_/g, ' ')} · {r.city} · {r.operator || 'no operator'}</span>
              </span>
              <span className={s.rowScreen}>
                {r.thing_name
                  ? <><LiveDot status={r.connection_status} at={r.last_heard_at} />{r.thing_name}</>
                  : <span className={s.unmapped}>none</span>}
                {r.thing_name && (deviceOut(r)
                  ? <span className={s.rowSub} style={{ color: 'var(--bad-text)', fontWeight: 600 }}>out of service — {r.device_lifecycle}</span>
                  : <span className={s.rowSub}>heard {relTime(r.last_heard_at)}</span>)}
              </span>
              <span className={s.rowScreen}>
                {r.sim_number ? `…${String(r.sim_number).slice(-6)}` : <span className={s.unmapped}>none</span>}
                {r.sim_number && <span className={s.rowSub}>{inrP(r.monthly_cost_paise)}/mo</span>}
              </span>
              <Track r={r} />
              <span className={s.verdictCol}>
                {r.drift
                  ? <span className={`${s.needs} ${s.needsBad}`}>operator drift</span>
                  : deviceOut(r)
                    ? <span className={`${s.needs} ${s.needsBad}`}>device out of service</span>
                    : missing.length
                      ? <span className={`${s.needs} ${s.needsMiss}`}>needs {missing.join(', ')}</span>
                      : <span className={`${s.needs} ${s.needsOk}`}>ready</span>}
                {gateWord(r)}
              </span>
              <Pill tone={LIFECYCLE_TONE[r.lifecycle_status]}>{r.lifecycle_status}</Pill>
            </div>
          </div>
        );
      })}
      {!reg.loading && !reg.error && !reg.rows.length && (
        <div style={{ padding: 'var(--s4)', color: 'var(--grow-text)', fontSize: 13 }}>
          {needs === 'work' && !anySelected(sel) ? 'Nothing needs work — the whole fleet is assembled.' : 'Nothing matches.'}
        </div>
      )}
      {reg.hasMore && (
        <button type="button" className={s.loadMore} onClick={reg.more} disabled={reg.loading}>
          {reg.loading ? 'loading…' : `Load more (${reg.rows.length} of ${reg.counts.total})`}
        </button>
      )}
      {reg.counts && <div className={s.resultsFoot}>Results from {reg.counts.total} premises</div>}
    </div>
  );
}

/* ── the peer registries ───────────────────────────────────────────────── */

function SetOwnerModal({ device, onDone, onClose }) {
  const [tenants, setTenants] = useState(null);
  const [pick, setPick] = useState('');
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/v2/tenants').then((r) => setTenants((r.data?.tenants || []).filter((t) => t.is_active !== false)))
      .catch(() => setTenants([]));
  }, []);
  const submit = async () => {
    setBusy(true); setNote(null);
    try {
      await api.post('/revenue/admin/ownership/transfer', {
        subject_type: 'device', subject_id: device.id, owner_tenant_id: pick || null,
      });
      onDone(); onClose();
    } catch (e) {
      setNote({ ok: false, text: e?.response?.data?.error || e.message });
      setBusy(false);
    }
  };
  return (
    <FormModal title={`Device owner — ${device.thing_name}`} onClose={onClose}>
      <p className={s.modalBody} style={{ margin: 0 }}>The device axis of money: this tenant earns the
        device share of this device&rsquo;s plays. Transfers are forward-only — past earnings keep
        their past owner. Current owner: <b>{device.owner_name || 'none — house'}</b>.</p>
      <div className={s.modalForm}>
        <span className={s.f}><label>New owner</label>
          <select value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">none — revert to house</option>
            {(tenants || []).map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_personal ? ' (personal)' : ''}</option>)}
          </select></span>
      </div>
      {note && <div className={s.badBanner}>{note.text}</div>}
      <div className={s.modalFoot}>
        <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
        <button type="button" className={s.btnCreate} disabled={busy} onClick={submit}>Transfer ownership</button>
      </div>
    </FormModal>
  );
}

function ScreensTab({ sel, toggle, toggleAll, openDrawer, q, refresh, onChanged }) {
  const reg = useRegistry('/v2/onboarding/screens', 'screens', sel, q, {}, refresh);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [ownerFor, setOwnerFor] = useState(null);
  const one = sel.screens.length === 1 ? reg.rows.find((r) => r.id === sel.screens[0]) : null;
  return (
    <Sheet reg={reg} noun="devices"
      footerExtra={<>
        {intakeOpen && <DeviceModal verb={null} eligibleParams={null} currentDeviceId={null}
          onMap={null} onChanged={onChanged} onClose={() => setIntakeOpen(false)} />}
        {ownerFor && <SetOwnerModal device={ownerFor} onDone={onChanged} onClose={() => setOwnerFor(null)} />}
      </>}
      toolbar={<>
        <button type="button" className={s.btnCreate} onClick={() => setIntakeOpen((v) => !v)}>
          <Plus size={14} aria-hidden="true" /> Register device
        </button>
        <button type="button" className={s.btnTool} disabled={!one}
                title={one ? `Set the device owner of ${one.thing_name}` : 'Select exactly one device'}
                onClick={() => one && setOwnerFor(one)}>
          <Building2 size={13} aria-hidden="true" /> Set owner
        </button>
        <ExportButton onClick={() => exportCsv('devices', [
        ['Device', (r) => r.thing_name], ['Status', (r) => (r.is_test_device ? 'test device' : r.connection_status || '')],
        ['Last heard', (r) => r.last_heard_at || 'never'], ['Owner', (r) => r.owner_name || ''], ['Premise', (r) => (r.deployment_id ? (r.premise_lifecycle === 'retired' ? `last rode ${r.deployed_at} (retired)` : r.deployed_at) : 'on the shelf')],
        ['Label photo', (r) => (r.hardware_label_image_key ? 'on record' : 'missing')],
        ['Lifecycle', (r) => r.device_lifecycle || ''],
      ], reg.rows, sel.screens)} /></>}
      facets={[
        ['online', 'live now (heard ≤10 min)', reg.counts?.online],
        ['deployed', 'deployed', reg.counts?.deployed],
        ['inventory', 'on the shelf', reg.counts?.in_inventory],
        ['procured', 'procured — never booted', reg.counts?.procured],
        ...(reg.counts?.out_of_service != null ? [['out', 'out of service', reg.counts.out_of_service]] : []),
      ]}
      note="A device exists the day it is bought: register it here at procurement (ownable from day one), and its own first boot claims the row. Pairing to a premise resolves through the hardware bridge at first play. Heard: green ≤10 min, amber ≤24 h, grey older."
      header={<HeaderRow gridClass={s.regRow} style={{ gridTemplateColumns: '22px minmax(170px, 1.1fr) minmax(120px, .8fr) minmax(110px, .8fr) minmax(170px, 1.2fr)' }}
        cols={[
          ['Device', 'Every registered device, deployed or on the shelf. Dot = the platform’s online flag.'],
          ['Last heard', 'The freshest exchange from the device — telemetry (≈1.5 min cadence while alive), presence, or connect. The online flag can lag this.'],
          ['Owner', 'The device-axis owner — who earns the device share of this device’s plays.'],
          ['Premise', 'The premise it rides, resolved through the hardware bridge.'],
        ]}
        pageIds={reg.rows.map((r) => r.id)} selIds={sel.screens}
        onToggleAll={() => toggleAll('screens', reg.rows.map((r) => r.id))} />}>
      {reg.rows.map((r) => (
        <div key={r.id} className={`${s.regRow} ${sel.screens.includes(r.id) ? s.rowSel : ''}`}
             style={{ gridTemplateColumns: '22px minmax(170px, 1.1fr) minmax(120px, .8fr) minmax(110px, .8fr) minmax(170px, 1.2fr)' }}
             role="button" tabIndex={0}
             onClick={() => openDrawer({ kind: 'device', id: r.id })}
             onKeyDown={(e) => { if (e.key === 'Enter') openDrawer({ kind: 'device', id: r.id }); }}>
          <Cb checked={sel.screens.includes(r.id)} onChange={() => toggle('screens', r.id)} label={`Select ${r.thing_name}`} />
          <span className={s.regPrimary}><LiveDot status={r.connection_status} at={r.last_heard_at} />{r.thing_name}
            {r.registration_status === 'procured'
              ? <small style={{ color: 'var(--warn-text)' }}>procured — never booted{r.device_model ? ` · ${r.device_model}` : ''}</small>
              : <small>{OUT_OF_SERVICE.includes(r.device_lifecycle) ? <span style={{ color: 'var(--bad-text, #b3261e)', fontWeight: 600 }}>{r.device_lifecycle} · </span> : null}{r.is_test_device ? 'test device · ' : ''}{r.device_model || 'model unknown'}{r.imei_tail ? ` · imei …${r.imei_tail}` : r.serial_tail ? ` · sn …${r.serial_tail}` : ''}{!r.hardware_label_image_key && <span style={{ color: 'var(--warn-text)' }}> · label photo missing</span>}</small>}</span>
          <span className={s.regMeta}>{r.registration_status === 'procured' ? 'awaiting first boot' : `heard ${relTime(r.last_heard_at)}`}</span>
          <span className={s.regMeta}>{r.owner_name ? <b>{r.owner_name}</b> : <span className={s.unmapped}>no owner — house</span>}</span>
          <span className={s.mapTo}>
            {r.deployment_id
              ? r.premise_lifecycle === 'retired'
                ? <span className={s.unmapped}>available — last rode <b>{r.premise_plate || r.deployed_at}</b> (retired)</span>
                : r.registration_status === 'procured'
                  ? <span className={s.unmapped}>installed at <b>{r.premise_plate || r.deployed_at}</b> — awaiting first boot</span>
                  : <><span className={s.mapArrow}>→</span>premise <b>{r.premise_plate || r.deployed_at}</b>{r.premise_lifecycle === 'parked' ? <span className={s.rowSub}> parked — delivery off</span> : null}</>
              : <span className={s.unmapped}>{r.registration_status === 'procured' ? 'in stock — awaiting first boot' : 'on the shelf'}</span>}
            {r.deployment_id && r.premise_plate && r.premise_plate !== r.deployed_at
              ? <span className={s.rowSub}>{r.deployed_at}</span> : null}
          </span>
        </div>
      ))}
    </Sheet>
  );
}

function SimsTab({ sel, toggle, toggleAll, openDrawer, q, refresh, onChanged }) {
  const reg = useRegistry('/v2/onboarding/sims', 'sims', sel, q, {}, refresh);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const one = sel.sims.length === 1 ? reg.rows.find((r) => r.id === sel.sims[0]) : null;
  return (
    <Sheet reg={reg} noun="SIMs"
      toolbar={
        <>
          <button type="button" className={s.btnCreate} onClick={() => setIntakeOpen((v) => !v)}>
            <Plus size={14} aria-hidden="true" /> Register SIM
          </button>
          <button type="button" className={s.btnTool} disabled={!one}
                  title={one ? 'Open the selected SIM' : 'Select exactly one SIM to edit'}
                  onClick={() => one && (one.deployment_id
                    ? openDrawer({ kind: 'deployment', id: one.deployment_id, section: 'sim' })
                    : openDrawer({ kind: 'attach-sim', row: one }))}>
            <Pencil size={13} aria-hidden="true" /> Edit
          </button>
          <ExportButton onClick={() => exportCsv('sims', [
            ['ICCID', (r) => r.sim_number], ['Provider', (r) => r.provider || ''],
            ['Mobile', (r) => r.mobile_number || ''], ['₹/month', (r) => (r.monthly_cost_paise == null ? '' : (Number(r.monthly_cost_paise) / 100).toFixed(2))],
            ['Plan', (r) => r.plan_name || ''],
            ['Status', (r) => r.status], ['Recovers from', (r) => r.recovers_from || ''], ['Premise', (r) => r.allocated_to || 'in stock'],
            ['Since', (r) => r.allocated_since || ''],
          ], reg.rows, sel.sims)} />
        </>
      }
      facets={[
        ['allocated', 'allocated', reg.counts?.allocated],
        ['stock', 'in stock', reg.counts?.in_stock],
      ]}
      note={null}
      footerExtra={intakeOpen && <SimIntake onDone={onChanged} onClose={() => setIntakeOpen(false)} />}
      header={<HeaderRow gridClass={s.regRow} style={{ gridTemplateColumns: '22px minmax(180px, 1.1fr) minmax(120px, .8fr) minmax(110px, .8fr) minmax(180px, 1.2fr)' }}
        cols={[
          ['SIM', 'ICCID as printed on the card; a SIM exists the day it is procured.'],
          ['Plan', 'The RECORDED plan cost. Today’s payout recovery bills the agreement’s configured rate (₹170) per allocated device — this field keeps the real cost on record.'],
          ['Recovers from', 'Whose payout carries this SIM — the premise owner of the premise it serves, deducted at the agreement’s rate before TDS.'],
          ['Premise', 'The open dated allocation; history accumulates, nothing is overwritten.'],
        ]}
        pageIds={reg.rows.map((r) => r.id)} selIds={sel.sims}
        onToggleAll={() => toggleAll('sims', reg.rows.map((r) => r.id))} />}>
      {reg.rows.map((r) => (
        <div key={r.id} className={`${s.regRow} ${sel.sims.includes(r.id) ? s.rowSel : ''}`}
             style={{ gridTemplateColumns: '22px minmax(180px, 1.1fr) minmax(120px, .8fr) minmax(110px, .8fr) minmax(180px, 1.2fr)' }}
             role="button" tabIndex={0}
             onClick={() => openDrawer({ kind: 'sim', id: r.id })}
             onKeyDown={(e) => { if (e.key === 'Enter') openDrawer({ kind: 'sim', id: r.id }); }}>
          <Cb checked={sel.sims.includes(r.id)} onChange={() => toggle('sims', r.id)} label={`Select SIM ${r.sim_number}`} />
          <span className={s.regPrimary}>{r.sim_number}<small>{r.provider || 'provider unknown'} {r.mobile_number ? `· ${r.mobile_number}` : ''}{!r.card_image_key && !r.envelope_image_key && <span style={{ color: 'var(--warn-text)' }}> · no photos</span>}</small></span>
          <span className={s.regMeta}>{r.monthly_cost_paise == null ? 'cost not recorded' : `${inrP(r.monthly_cost_paise)}/month`}{r.plan_name ? ` · ${r.plan_name}` : ''} · {String(r.status).replace(/_/g, ' ')}</span>
          <span className={s.regMeta}>{r.recovers_from ? <b>{r.recovers_from}</b> : '—'}</span>
          <span className={s.mapTo}>
            {r.deployment_id
              ? <><span className={s.mapArrow}>→</span>in <b>{r.allocated_to}</b> since {fmtDate(r.allocated_since)}</>
              : <span className={s.unmapped}>in stock — click to allocate</span>}
          </span>
        </div>
      ))}
    </Sheet>
  );
}

const VEHICLE_DOC_DATES = [
  ['rc_expires_on', 'RC'], ['insurance_expires_on', 'insurance'], ['puc_expires_on', 'PUC'],
  ['fitness_expires_on', 'fitness'], ['permit_expires_on', 'permit'],
];
function nearestDocName(r) {
  let best = null;
  VEHICLE_DOC_DATES.forEach(([k, name]) => {
    if (r[k] && (!best || r[k] < best.d)) best = { d: r[k], name };
  });
  return best ? best.name : null;
}

function VehicleIntake({ onDone, onClose }) {
  const [f, setF] = useState({ registration_number: '', make: '', model: '', model_year: '' });
  const [note, setNote] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setNote(null);
    try {
      await api.post('/v2/onboarding/vehicles', {
        registration_number: f.registration_number,
        make: f.make || null, model: f.model || null, model_year: f.model_year || null,
      });
      onDone(); onClose();
    } catch (e) {
      setNote({ ok: false, text: e?.response?.data?.error || e.message });
    }
  };
  return (
    <FormModal title="Add vehicle" onClose={onClose}>
      <p className={s.modalBody} style={{ margin: 0 }}>The compliance registry — document expiries
        (RC, insurance, PUC, fitness, permit) are recorded as they arrive; only identity is needed here.</p>
      <div className={s.modalForm}>
        <span className={s.f}><label>Registration number</label>
          <input value={f.registration_number} onChange={set('registration_number')} placeholder="MH12AB1234" /></span>
        <div className={s.twoCol}>
          <span className={s.f}><label>Make</label>
            <input value={f.make} onChange={set('make')} placeholder="Citroen" /></span>
          <span className={s.f}><label>Model</label>
            <input value={f.model} onChange={set('model')} placeholder="ec3" /></span>
        </div>
        <div className={s.twoCol}>
          <span className={s.f}><label>Model year</label>
            <input type="number" value={f.model_year} onChange={set('model_year')} placeholder="2024" /></span>
          <span />
        </div>
      </div>
      {note && <div className={s.badBanner}>{note.text}</div>}
      <div className={s.modalFoot}>
        <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
        <button type="button" className={s.btnCreate} disabled={!f.registration_number.trim()} onClick={submit}>
          <Plus size={14} aria-hidden="true" /> Add vehicle
        </button>
      </div>
    </FormModal>
  );
}

function VehiclesTab({ sel, toggle, toggleAll, openDrawer, q, refresh, onChanged }) {
  const reg = useRegistry('/v2/onboarding/vehicles', 'vehicles', sel, q, {}, refresh);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const one = sel.vehicles.length === 1 ? reg.rows.find((r) => r.id === sel.vehicles[0]) : null;
  return (
    <Sheet reg={reg} noun="vehicles"
      footerExtra={intakeOpen && <VehicleIntake onDone={onChanged} onClose={() => setIntakeOpen(false)} />}
      toolbar={
        <>
          <button type="button" className={s.btnCreate} onClick={() => setIntakeOpen((v) => !v)}>
            <Plus size={14} aria-hidden="true" /> Add vehicle
          </button>
          <button type="button" className={s.btnTool} disabled={!one}
                  title={one ? 'Open the selected vehicle' : 'Select exactly one vehicle to edit'}
                  onClick={() => one && (one.deployment_id
                    ? openDrawer({ kind: 'deployment', id: one.deployment_id, section: 'vehicle' })
                    : openDrawer({ kind: 'attach-vehicle', row: one }))}>
            <Pencil size={13} aria-hidden="true" /> Edit
          </button>
          <ExportButton onClick={() => exportCsv('vehicles', [
            ['Registration', (r) => r.registration_number], ['Make', (r) => [r.make, r.model, r.model_year].filter(Boolean).join(' ')],
            ['Nearest doc', (r) => nearestDocName(r) || ''], ['Nearest doc expiry', (r) => r.nearest_doc_expiry || ''], ['Driver', (r) => r.driver_name || ''], ['Premise', (r) => r.linked_to || 'unlinked'],
          ], reg.rows, sel.vehicles)} />
        </>
      }
      facets={[
        ['linked', 'linked', reg.counts ? Number(reg.counts.total) - Number(reg.counts.unlinked) : null],
        ['unlinked', 'unlinked', reg.counts?.unlinked],
        ['expiring', 'docs expiring ≤30d', reg.counts?.expiring],
      ]}
      header={<HeaderRow gridClass={s.regRow} style={{ gridTemplateColumns: '22px minmax(160px, 1fr) minmax(160px, 1fr) minmax(120px, .8fr) minmax(170px, 1.2fr)' }}
        cols={[
          ['Vehicle', 'The compliance registry — plate, make, model.'],
          ['Documents', 'The document expiring soonest, by name, across RC · insurance · PUC · fitness · permit.'],
          ['Driver', 'The active driver on the premise this vehicle carries.'],
          ['Premise', 'The in-cab premise this vehicle carries.'],
        ]}
        pageIds={reg.rows.map((r) => r.id)} selIds={sel.vehicles}
        onToggleAll={() => toggleAll('vehicles', reg.rows.map((r) => r.id))} />}>
      {reg.rows.map((r) => {
        const d = daysTo(r.nearest_doc_expiry);
        const docName = nearestDocName(r);
        return (
          <div key={r.id} className={`${s.regRow} ${sel.vehicles.includes(r.id) ? s.rowSel : ''}`}
               style={{ gridTemplateColumns: '22px minmax(160px, 1fr) minmax(160px, 1fr) minmax(120px, .8fr) minmax(170px, 1.2fr)' }}
               role="button" tabIndex={0}
               onClick={() => openDrawer({ kind: 'vehicle', id: r.id })}
               onKeyDown={(e) => { if (e.key === 'Enter') openDrawer({ kind: 'vehicle', id: r.id }); }}>
            <Cb checked={sel.vehicles.includes(r.id)} onChange={() => toggle('vehicles', r.id)} label={`Select ${r.registration_number}`} />
            <span className={s.regPrimary}>{r.registration_number}<small>{[[r.make, r.model, r.model_year].filter(Boolean).join(' ') || 'make unknown', r.colour, r.fuel_type].filter(Boolean).join(' · ')}</small></span>
            <span className={s.regMeta}>
              {r.nearest_doc_expiry
                ? <span style={d != null && d <= 30 ? { color: 'var(--warn-text)', fontWeight: 600 } : undefined}>
                    {docName || 'docs'}: {fmtDate(r.nearest_doc_expiry)}{d != null && d <= 30 ? ` (${d}d)` : ''}
                  </span>
                : 'no document expiries recorded'}
            </span>
            <span className={s.regMeta}>{r.driver_name || <span style={{ color: 'var(--ink-faint)' }}>no driver</span>}</span>
            <span className={s.mapTo}>
              {r.deployment_id
                ? <><span className={s.mapArrow}>→</span>premise <b>{r.linked_to}</b></>
                : <span className={s.unmapped}>unlinked — click to link</span>}
            </span>
          </div>
        );
      })}
    </Sheet>
  );
}

function DriversTab({ sel, toggle, toggleAll, openDrawer, q, refresh, onChanged }) {
  const reg = useRegistry('/v2/onboarding/drivers', 'drivers', sel, q, {}, refresh);
  const [intakeOpen, setIntakeOpen] = useState(false);
  return (
    <Sheet reg={reg} noun="driver assignments"
      footerExtra={intakeOpen && <DriverIntake onDone={onChanged} onClose={() => setIntakeOpen(false)} />}
      toolbar={<>
        <button type="button" className={s.btnCreate} onClick={() => setIntakeOpen((v) => !v)}>
          <Plus size={14} aria-hidden="true" /> Add driver
        </button>
        <ExportButton onClick={() => exportCsv('drivers', [
        ['Driver', (r) => r.driver_name], ['Mobile', (r) => r.driver_mobile || ''],
        ['Assigned', (r) => r.assigned_at || ''], ['Unassigned', (r) => r.unassigned_at || ''],
        ['Cab', (r) => r.cab_plate || r.assigned_to || ''],
      ], reg.rows, sel.drivers)} /></>}
      facets={[
        ['assigned', 'assigned', reg.counts?.assigned],
        ['unassigned', 'past assignments', reg.counts ? Number(reg.counts.total) - Number(reg.counts.assigned) : null],
      ]}
      header={<HeaderRow gridClass={s.regRow}
        cols={[
          ['Driver', 'Assignments are dated; past assignments keep their interval.'],
          ['Assignment', 'Active since, or the closed interval.'],
          ['Cab', 'The vehicle plate; when the premise label differs, it is shown alongside.'],
          ['', null],
        ]}
        pageIds={reg.rows.map((r) => r.id)} selIds={sel.drivers}
        onToggleAll={() => toggleAll('drivers', reg.rows.map((r) => r.id))} />}>
      {reg.rows.map((r) => (
        <div key={r.id} className={`${s.regRow} ${sel.drivers.includes(r.id) ? s.rowSel : ''}`}
             role="button" tabIndex={0}
             onClick={() => openDrawer({ kind: 'driver', id: r.id })}
             onKeyDown={(e) => { if (e.key === 'Enter') openDrawer({ kind: 'driver', id: r.id }); }}>
          <Cb checked={sel.drivers.includes(r.id)} onChange={() => toggle('drivers', r.id)} label={`Select ${r.driver_name}`} />
          <span className={s.regPrimary} style={{ fontFamily: 'inherit' }}>{r.driver_name}<small>{r.driver_mobile || ''}</small></span>
          <span className={s.regMeta}>{r.is_active ? `since ${fmtDate(r.assigned_at)}` : `${fmtDate(r.assigned_at)} → ${fmtDate(r.unassigned_at)}`}</span>
          <span className={s.mapTo}>
            {r.cab_plate || r.assigned_to
              ? <><span className={s.mapArrow}>→</span>drives <b>{r.cab_plate || r.assigned_to}</b>{r.cab_plate && r.assigned_to && r.assigned_to !== r.cab_plate ? <span className={s.rowSub}> premise {r.assigned_to}</span> : null}</>
              : <span className={s.unmapped}>no cab resolved</span>}
          </span>
          <span className={s.hint}><NavLink to="/drivers" title="Manage in classic Drivers & vehicles" onClick={(e) => e.stopPropagation()}>manage</NavLink></span>
        </div>
      ))}
    </Sheet>
  );
}

function PremiseOwnersTab({ sel, toggle, q }) {
  const selKey = JSON.stringify(selParams(sel, 'premise_owners'));
  const { data, error, loading, reload } = useApi(
    () => api.get('/v2/onboarding/premise-owners', { params: JSON.parse(selKey) }).then((r) => {
      if (!r.data || !Array.isArray(r.data.rows)) throw new Error('The server did not answer with the premise-owners registry.');
      return r.data.rows;
    }), [selKey]);
  const rows = (data || []).filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (sel.premise_owners.includes(b.id) ? 1 : 0) - (sel.premise_owners.includes(a.id) ? 1 : 0));
  const grid = { gridTemplateColumns: '22px minmax(150px, 200px) minmax(200px, 1.2fr) minmax(130px, .8fr) 170px' };
  return (
    <div className={s.sheet}>
      <div className={s.toolbar}>
        <button type="button" className={s.btnTool}
                onClick={() => exportCsv('premise-owners', [
                  ['Owner', (r) => r.name], ['Premises owned', (r) => r.premises_owned],
                  ['Registry nodes', (r) => r.registry_nodes], ['Drift nodes', (r) => r.drift_nodes],
                  ['SIM plans recorded ₹/mo (payout bills the agreement rate)', (r) => (Number(r.sim_month_paise) / 100).toFixed(2)],
                  ['Terms', (r) => (r.acceptances > 0 ? (r.has_own_terms ? 'own' : 'defaults') : 'no TnC')],
                ], rows, sel.premise_owners)}>
          <Download size={13} aria-hidden="true" /> Export
        </button>
        <span className={s.spacer} />
        <span className={s.hint}>earns the premise share of plays on their premises · check a row to narrow every other tab</span>
      </div>
      <div className={`${s.regRow} ${s.thRow}`} style={grid}>
        <span />
        <span className={s.th}>Premise owner</span>
        <span className={s.th}>Premises
          <span className={s.helpDot} title="Owned = current money-plane ownership (what earns). Registry = nodes merely tagged with this tenant; drift counts tags disagreeing with the money owner."><Info size={11} /></span>
        </span>
        <span className={s.th}>SIM plans (recorded)
          <span className={s.helpDot} title="Sum of the RECORDED plan costs of SIMs serving their premises. The payout deducts the agreement’s configured rate (₹170 per device-month) before TDS — not this number."><Info size={11} /></span>
        </span>
        <span className={s.th}>Terms</span>
      </div>
      {loading && <div style={{ padding: 'var(--s4)' }}><Loading what="premise owners" /></div>}
      {error && <div style={{ padding: 'var(--s4)' }}><ErrorState error={error} onRetry={reload} /></div>}
      {rows.map((r) => (
        <div key={r.id} className={`${s.regRow} ${sel.premise_owners.includes(r.id) ? s.rowSel : ''}`} style={grid}
             role="button" tabIndex={0}
             onClick={() => toggle('premise_owners', r.id)}
             onKeyDown={(e) => { if (e.key === 'Enter') toggle('premise_owners', r.id); }}>
          <Cb checked={sel.premise_owners.includes(r.id)} onChange={() => toggle('premise_owners', r.id)} label={`Select ${r.name}`} />
          <span className={s.regPrimary} style={{ fontFamily: 'inherit' }}>
            {r.name}{r.is_personal ? <small>personal tenant</small> : null}
          </span>
          <span className={s.regMeta}>
            <b>{r.premises_owned}</b> owned · {r.registry_nodes} registry{r.premises_owned === 0 && r.registry_nodes > 0 ? <span className={s.unmapped}> — house money</span> : ''}
            {r.drift_nodes > 0 ? <span className={s.unmapped}> · {r.drift_nodes} DRIFT</span> : ''}
          </span>
          <span className={s.regMeta}>{Number(r.sim_month_paise) > 0 ? <b>{inrP(r.sim_month_paise)}/mo</b> : '—'}</span>
          <span className={s.hint}>
            {r.acceptances > 0 ? (r.has_own_terms ? 'own terms' : 'defaults') : 'no TnC'}
            {' · '}<NavLink to={`/v2/demand/tenants?id=${r.id}`} onClick={(e) => e.stopPropagation()}>entity</NavLink>
          </span>
        </div>
      ))}
      <p className={s.hint} style={{ padding: '8px var(--s3)' }}>
        A tenant with registry nodes but nothing owned runs premises whose earnings stay house money —
        transfer ownership on <NavLink to="/v2/payouts/ownership">Payouts › Ownership</NavLink> to change that.
      </p>
      <div className={s.resultsFoot}>Results from {rows.length} premise owners</div>
    </div>
  );
}

function DeviceOwnersTab({ sel, toggle, q }) {
  const selKey = JSON.stringify(selParams(sel, 'device_owners'));
  const { data, error, loading, reload } = useApi(
    () => api.get('/v2/onboarding/device-owners', { params: JSON.parse(selKey) }).then((r) => {
      if (!r.data || !Array.isArray(r.data.rows)) throw new Error('The server did not answer with the device-owners registry.');
      return r.data.rows;
    }), [selKey]);
  const rows = (data || []).filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (sel.device_owners.includes(b.id) ? 1 : 0) - (sel.device_owners.includes(a.id) ? 1 : 0));
  const grid = { gridTemplateColumns: '22px minmax(150px, 200px) minmax(180px, 1fr) minmax(200px, 1.2fr) 170px' };
  return (
    <div className={s.sheet}>
      <div className={s.toolbar}>
        <button type="button" className={s.btnTool}
                onClick={() => exportCsv('device-owners', [
                  ['Owner', (r) => r.name], ['Devices owned', (r) => r.screens_owned],
                  ['Procured (never booted)', (r) => r.procured_owned || 0],
                  ['Riding', (r) => r.riding], ['Live now (heard ≤10 min)', (r) => r.online_now],
                  ['On others’ premises', (r) => r.on_others_premises],
                  ['Terms', (r) => (r.acceptances > 0 ? (r.has_own_terms ? 'own' : 'defaults') : 'no TnC')],
                ], rows, sel.device_owners)}>
          <Download size={13} aria-hidden="true" /> Export
        </button>
        <span className={s.spacer} />
        <span className={s.hint}>earns the device share of their devices&rsquo; plays · check a row to narrow every other tab</span>
      </div>
      <div className={`${s.regRow} ${s.thRow}`} style={grid}>
        <span />
        <span className={s.th}>Device owner</span>
        <span className={s.th}>Devices
          <span className={s.helpDot} title="Current money-plane ownership on the device axis — a shelf device is still owned and earns once it rides. Procured = owned at purchase, never booted (stock on order)."><Info size={11} /></span>
        </span>
        <span className={s.th}>Where they ride
          <span className={s.helpDot} title="Riding = resolved onto a premise via the hardware bridge; 'on others' counts screens riding premises owned by a DIFFERENT tenant — the investor case."><Info size={11} /></span>
        </span>
        <span className={s.th}>Terms</span>
      </div>
      {loading && <div style={{ padding: 'var(--s4)' }}><Loading what="device owners" /></div>}
      {error && <div style={{ padding: 'var(--s4)' }}><ErrorState error={error} onRetry={reload} /></div>}
      {rows.map((r) => (
        <div key={r.id} className={`${s.regRow} ${sel.device_owners.includes(r.id) ? s.rowSel : ''}`} style={grid}
             role="button" tabIndex={0}
             onClick={() => toggle('device_owners', r.id)}
             onKeyDown={(e) => { if (e.key === 'Enter') toggle('device_owners', r.id); }}>
          <Cb checked={sel.device_owners.includes(r.id)} onChange={() => toggle('device_owners', r.id)} label={`Select ${r.name}`} />
          <span className={s.regPrimary} style={{ fontFamily: 'inherit' }}>
            {r.name}{r.is_personal ? <small>personal tenant</small> : null}
          </span>
          <span className={s.regMeta}><b>{r.screens_owned}</b> owned · <span style={{ color: 'var(--grow-text)' }}>{r.online_now} live now</span>{Number(r.procured_owned) > 0 ? <> · <span style={{ color: 'var(--warn-text)' }}>{r.procured_owned} procured</span></> : ''}</span>
          <span className={s.regMeta}>
            {r.riding} riding · {r.screens_owned - r.riding} on the shelf
            {r.on_others_premises > 0 ? <> · <b>{r.on_others_premises}</b> on others&rsquo; premises</> : ''}
          </span>
          <span className={s.hint}>
            {r.acceptances > 0 ? (r.has_own_terms ? 'own terms' : 'defaults') : 'no TnC'}
            {' · '}<NavLink to={`/v2/demand/tenants?id=${r.id}`} onClick={(e) => e.stopPropagation()}>entity</NavLink>
          </span>
        </div>
      ))}
      <p className={s.hint} style={{ padding: '8px var(--s3)' }}>
        The two axes are separate on purpose: an investor&rsquo;s devices can ride another fleet&rsquo;s
        cabs — the premise share and the device share then go to different tenants.
      </p>
      <div className={s.resultsFoot}>Results from {rows.length} device owners</div>
    </div>
  );
}

/* ── SIM intake ────────────────────────────────────────────────────────── */

function DocField({ label, endpoint, kind, value, onKey, existingKey }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [name, setName] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    let live = true;
    if (!value && existingKey) {
      api.get('/v2/onboarding/doc-url', { params: { key: existingKey } })
        .then((r) => { if (live) { setPreview(r.data.url); setName('current photo'); } }).catch(() => {});
    }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingKey]);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const pre = (await api.post(endpoint, { kind, contentType: file.type || 'image/jpeg' })).data;
      const put = await fetch(pre.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'image/jpeg' }, body: file });
      if (!put.ok) throw new Error(`upload failed (${put.status})`);
      setName(file.name);
      setPreview(URL.createObjectURL(file));
      onKey(pre.key);
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className={s.f}>
      <label>{label}</label>
      <label className={`${s.drop} ${value ? s.dropDone : ''}`}>
        <input type="file" accept="image/*" onChange={pick} disabled={busy} aria-label={label} />
        {(value || (existingKey && preview))
          ? <>{preview
                ? <img src={preview} alt="" className={s.dropThumb} />
                : <CheckCircle2 size={20} className={s.dropIcon} style={{ color: 'var(--grow-text)' }} aria-hidden="true" />}
              <span className={s.dropText}>{name || 'uploaded'}</span>
              <span className={s.dropHint}>{value ? 'click to replace' : 'on record — click to replace'}</span></>
          : <><ImagePlus size={20} className={s.dropIcon} aria-hidden="true" />
              <span className={s.dropText}>{busy ? 'uploading…' : 'Click to add a photo'}</span>
              <span className={s.dropHint}>{err || 'JPEG · PNG · WebP · HEIC'}</span></>}
      </label>
    </span>
  );
}

function SimIntake({ onDone, onClose }) {
  const [f, setF] = useState({ sim_number: '', provider: '', mobile_number: '', imsi_number: '',
    plan_name: '', cost: '', notes: '', card_image_key: null, envelope_image_key: null });
  const [note, setNote] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setNote(null);
    try {
      await api.post('/v2/onboarding/sims', {
        sim_number: f.sim_number,
        provider: f.provider || null,
        mobile_number: f.mobile_number || null,
        imsi_number: f.imsi_number || null,
        plan_name: f.plan_name || null,
        notes: f.notes || null,
        card_image_key: f.card_image_key,
        envelope_image_key: f.envelope_image_key,
        monthly_cost_paise: f.cost.trim() === '' ? null : Math.round(parseFloat(f.cost) * 100),
      });
      onDone(); onClose();
    } catch (e) {
      setNote({ ok: false, text: e?.response?.data?.error || e.message });
    }
  };
  return (
    <FormModal title="Register SIM" onClose={onClose}>
      <p className={s.modalBody} style={{ margin: 0 }}>A SIM exists the day it is procured, deployment or not.
        Record the REAL monthly cost — today&rsquo;s payout recovery bills the agreement&rsquo;s configured rate (₹170), and this field is the truth that lets that decree change later.</p>
      <div className={s.modalForm}>
        <span className={s.f}><label>ICCID (as printed on the card)</label>
          <input value={f.sim_number} onChange={set('sim_number')} placeholder="8991…" /></span>
        <div className={s.twoCol}>
          <span className={s.f}><label>Provider</label>
            <input value={f.provider} onChange={set('provider')} placeholder="Jio / Airtel…" /></span>
          <span className={s.f}><label>Mobile number</label>
            <input value={f.mobile_number} onChange={set('mobile_number')} /></span>
        </div>
        <div className={s.twoCol}>
          <span className={s.f}><label>IMSI</label>
            <input value={f.imsi_number} onChange={set('imsi_number')} /></span>
          <span className={s.f}><label>Plan name</label>
            <input value={f.plan_name} onChange={set('plan_name')} placeholder="JioIoT 1GB…" /></span>
        </div>
        <div className={s.twoCol}>
          <span className={s.f}><label>₹ / month (real plan cost)</label>
            <input type="number" value={f.cost} onChange={set('cost')} placeholder="170" /></span>
          <span />
        </div>
        <div className={s.twoCol}>
          <DocField label="SIM card photo" endpoint="/v2/onboarding/sims/upload-url" kind="card" value={f.card_image_key}
                         onKey={(k) => setF((prev) => ({ ...prev, card_image_key: k }))} />
          <DocField label="Envelope photo (PIN/PUK)" endpoint="/v2/onboarding/sims/upload-url" kind="envelope" value={f.envelope_image_key}
                         onKey={(k) => setF((prev) => ({ ...prev, envelope_image_key: k }))} />
        </div>
        <span className={s.f}><label>Notes</label>
          <textarea value={f.notes} onChange={set('notes')} placeholder="batch, vendor, anything ops will need later…" /></span>
      </div>
      {note && <div className={s.badBanner}>{note.text}</div>}
      <div className={s.modalFoot}>
        <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
        <button type="button" className={s.btnCreate} disabled={!f.sim_number.trim()} onClick={submit}>
          <Plus size={14} aria-hidden="true" /> Register SIM
        </button>
      </div>
    </FormModal>
  );
}

function PremiseIntake({ onDone, onClose }) {
  const [f, setF] = useState({ node_name: '', platform: 'in_cab', city: 'mumbai', tenant_id: '' });
  const [tenants, setTenants] = useState(null);
  const [note, setNote] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => {
    // ANY tenant can operate a premise — the full registry, not just tenants
    // that already operate one (founder, 5 Sep)
    api.get('/v2/tenants').then((r) => setTenants((r.data?.tenants || []).filter((t) => t.is_active !== false)))
      .catch(() => setTenants([]));
  }, []);
  const submit = async () => {
    setNote(null);
    try {
      await api.post('/v2/onboarding/premises', {
        node_name: f.node_name, platform: f.platform, city: f.city, tenant_id: f.tenant_id || null,
      });
      setNote(null); onDone(); onClose();
    } catch (e) {
      setNote({ ok: false, text: e?.response?.data?.error || e.message });
    }
  };
  return (
    <FormModal title="New premise" onClose={onClose}>
      <p className={s.modalBody} style={{ margin: 0 }}>A place that earns, born the day the location is
        signed. Money-plane ownership is separate — set it on Payouts › Ownership.</p>
      <div className={s.modalForm}>
        <span className={s.f}><label>Name — the plate for a cab, the venue for DOOH</label>
          <input value={f.node_name} onChange={set('node_name')} placeholder="Taxi Pune MH12AB1234 · Dadar Station TV 3" /></span>
        <div className={s.twoCol}>
          <span className={s.f}><label>Premise type</label>
            <select value={f.platform} onChange={set('platform')}>
              <option value="in_cab">in-cab</option>
              <option value="transit_tv">transit TV</option>
              <option value="billboard">billboard</option>
              <option value="bus_stop">bus stop</option>
            </select></span>
          <span className={s.f}><label>City</label>
            <select value={f.city} onChange={set('city')}>
              {['mumbai', 'pune', 'delhi', 'hyderabad', 'dubai'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select></span>
        </div>
        <span className={s.f}><label>Operator (registry tag) — any tenant</label>
          <select value={f.tenant_id} onChange={set('tenant_id')}>
            <option value="">none — house</option>
            {(tenants || []).map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_personal ? ' (personal)' : ''}</option>)}
          </select></span>
      </div>
      {note && <div className={s.badBanner}>{note.text}</div>}
      <div className={s.modalFoot}>
        <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
        <button type="button" className={s.btnCreate} disabled={!f.node_name.trim()} onClick={submit}>
          <Plus size={14} aria-hidden="true" /> Create premise
        </button>
      </div>
    </FormModal>
  );
}

/* ── modal primitives: Listbox, FormModal, ConfirmModal ────────────────── */

// BM Field.jsx: 'PREFER Listbox over <Select>' — the custom dropdown, in
// v2 tokens. Options: [{ value, label, hint? }].
function Listbox({ value, onChange, options, placeholder = 'choose…', ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = React.useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const sel = options.find((o) => String(o.value) === String(value));
  return (
    <span className={s.lbWrap} ref={rootRef}>
      <button type="button" className={s.lbBtn} aria-haspopup="listbox" aria-expanded={open}
              aria-label={ariaLabel || placeholder} onClick={() => setOpen((v) => !v)}>
        {sel ? <span>{sel.label}</span> : <span className={s.lbPlaceholder}>{placeholder}</span>}
        <ChevronDown size={15} style={{ color: 'var(--ink-mute)', flex: 'none' }} aria-hidden="true" />
      </button>
      {open && (
        <div className={s.lbPop} role="listbox">
          {options.map((o) => (
            <button key={String(o.value)} type="button" role="option"
                    aria-selected={String(o.value) === String(value)}
                    className={`${s.lbOpt} ${String(o.value) === String(value) ? s.lbSel : ''}`}
                    onClick={() => { onChange(o.value); setOpen(false); }}>
              <span>{o.label}{o.hint && <small>{o.hint}</small>}</span>
              {String(o.value) === String(value) && <Check size={14} style={{ color: 'var(--iris-text)', flex: 'none' }} aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

const TYPE_OPTS = [
  { value: 'in_cab', label: 'in-cab' }, { value: 'transit_tv', label: 'transit TV' },
  { value: 'billboard', label: 'billboard' }, { value: 'bus_stop', label: 'bus stop' },
];
const CITY_OPTS = ['mumbai', 'pune', 'delhi', 'hyderabad', 'dubai'].map((c) => ({ value: c, label: c }));
const STATUS_OPTS = LIFECYCLES.map((l) => ({ value: l, label: l }));

function FormModal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className={s.modalScrim} onClick={onClose} role="presentation">
      <div className={`${s.modal} ${s.modalWide}`} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalTitle}>{title}</div>
        {children}
      </div>
    </div>,
    modalHost()
  );
}

function ConfirmModal({ title, body, confirmLabel, danger, onConfirm, onCancel }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return createPortal(
    <div className={s.modalScrim} onClick={onCancel} role="presentation">
      <div className={s.modal} role="dialog" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalTitle}>{title}</div>
        <div className={s.modalBody}>{body}</div>
        <div className={s.modalFoot}>
          <button type="button" className={s.btnTool} onClick={onCancel}>Cancel</button>
          <button type="button" className={danger ? s.btnDanger : s.btnCreate} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>,
    modalHost()
  );
}

// What each lifecycle target really means — said before the click lands.
const OUT_OF_SERVICE = ['retired', 'lost', 'rma'];
const DEVICE_LIFECYCLE_WORDS = {
  active: <>Serving switches <b>back on</b> for this device (is_active becomes true). It resumes pulling manifests wherever it is bridged; campaign terms still decide what plays.</>,
  retired: <>Out of service for good. Serving switches <b>off</b> (is_active becomes false) — the manifest empties within ~30 s and the pricer never prices what does not play. Plays already recorded stay on the books; nothing is unbilled retroactively.</>,
  lost: <>Marks the tablet as lost. Serving switches <b>off</b> (is_active becomes false) — if it ever phones home it stays dark until you reactivate it here. Recorded plays stay on the books.</>,
  rma: <>In for repair. Serving switches <b>off</b> (is_active becomes false) while it is away; mark it active when it returns. Recorded plays stay on the books.</>,
};
const LIFECYCLE_CONSEQUENCE = (row, to) => {
  const bits = [];
  if (to === 'retired' || to === 'parked') {
    bits.push(<p key="op" style={{ margin: '0 0 8px' }}>
      <b>Ad delivery stops here</b> on the device&rsquo;s next manifest pull (≈30 s): the manifest
      excludes premises marked retired or parked. Plays stop, so billing and earnings stop —
      the books only ever record actual plays, so nothing is fabricated or erased.
    </p>);
    bits.push(<p key="keep" style={{ margin: '0 0 8px' }}>
      Nothing else is detached automatically:{' '}
      {row.thing_name ? <>device <b>{row.thing_name}</b> stays active as an asset (redeploy it anywhere); </> : null}
      {row.sim_id ? <>SIM <b>{row.sim_number}</b> stays allocated and its {inrP(row.monthly_cost_paise)}/month
        keeps recovering from <b>{row.money_operator || row.registry_operator || 'the premise owner'}</b>; </> : null}
      {row.driver_name ? <>driver <b>{row.driver_name}</b> stays assigned; </> : null}
      {row.registration_number ? <>vehicle <b>{row.registration_number}</b> stays linked; </> : null}
      ownership intervals stay open. If this premise is truly done, return the SIM and unlink
      the vehicle from the drawer — each is its own dated, audited step.
    </p>);
  } else {
    bits.push(<p key="op" style={{ margin: '0 0 8px' }}>
      Moving to <b>{to}</b> makes this premise servable again: the device&rsquo;s next manifest
      pull resumes ad delivery, and plays bill and earn under the same ownership and gate.
    </p>);
  }
  bits.push(<p key="log" style={{ margin: 0 }}>
    The change is recorded in the action log with your name.
  </p>);
  return <>{bits}</>;
};

/* ── generic audited EDIT modal: fields config → form → confirm listing
   the diffs → POST /<entity>/:id/edit (field-granular audit server-side).
   One shape for SIM / vehicle / driver — never a new invention. ────────── */

function EntityEditModal({ title, endpoint, fields, photoFields = [], initial, onChanged, onClose }) {
  const dateNorm = (v) => (v && /\d{4}-\d{2}-\d{2}/.test(String(v)) ? String(v).slice(0, 10) : (v ?? ''));
  const init = {};
  fields.forEach((fd) => { init[fd.key] = fd.type === 'date' ? dateNorm(initial[fd.key]) : initial[fd.key]; });
  const [f, setF] = useState(init);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const save = () => {
    const diffs = fields.filter((fd) => !fd.readonly && String(f[fd.key] ?? '') !== String(init[fd.key] ?? ''));
    const photoDiffs = photoFields.filter((pf) => f[pf.key]);
    if (!diffs.length && !photoDiffs.length) { setNote('Nothing changed.'); return; }
    setConfirm({
      body: <>
        {diffs.map((fd) => <p key={fd.key} style={{ margin: '0 0 8px' }}>{fd.label}:{' '}
          <b>{String(init[fd.key] ?? '') || 'none'}</b> → <b>{String(f[fd.key] ?? '') || 'none'}</b></p>)}
        {photoDiffs.map((pf) => <p key={pf.key} style={{ margin: '0 0 8px' }}>{pf.label} replaced.</p>)}
        <p style={{ margin: 0 }}>Every changed field is logged — old value, new value, your name.</p>
      </>,
      onYes: async () => {
        setBusy(true); setNote(null);
        try {
          const body = {};
          diffs.forEach((fd) => { body[fd.key] = f[fd.key]; });
          photoDiffs.forEach((pf) => { body[pf.key] = f[pf.key]; });
          await api.post(endpoint, body);
          onChanged(); onClose();
        } catch (e) { setNote(e?.response?.data?.error || e.message); setBusy(false); setConfirm(null); }
      },
    });
  };
  return (
    <FormModal title={title} onClose={onClose}>
      {confirm && <ConfirmModal title={`Save changes?`} body={confirm.body} confirmLabel="Save changes"
                                onCancel={() => setConfirm(null)} onConfirm={confirm.onYes} />}
      <div className={s.modalForm}>
        {fields.map((fd) => (
          <span key={fd.key} className={s.f}>
            <label>{fd.label}{fd.readonly ? ' (locked)' : ''}</label>
            {fd.textarea
              ? <textarea value={f[fd.key] ?? ''} disabled={fd.readonly} onChange={(e) => setF({ ...f, [fd.key]: e.target.value })} />
              : <input value={f[fd.key] ?? ''} disabled={fd.readonly} type={fd.type || 'text'}
                       onChange={(e) => setF({ ...f, [fd.key]: e.target.value })} />}
            {fd.hint && <span className={s.hint}>{fd.hint}</span>}
          </span>
        ))}
        {photoFields.length > 0 && (
          <div className={s.twoCol}>
            {photoFields.map((pf) => (
              <DocField key={pf.key} label={pf.label} endpoint={pf.endpoint} kind={pf.kind}
                        value={f[pf.key]} existingKey={pf.existingKey}
                        onKey={(k) => setF((p2) => ({ ...p2, [pf.key]: k }))} />
            ))}
          </div>
        )}
      </div>
      {note && <div className={s.badBanner}>{note}</div>}
      <div className={s.modalFoot}>
        <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
        <button type="button" className={s.btnCreate} disabled={busy} onClick={save}>Save changes</button>
      </div>
    </FormModal>
  );
}

/* compact stock picker: search + server pages, for slot-side picking */
function StockPickModal({ title, endpoint, params, mapRow, verb, onPick, onClose }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState({ rows: [], offset: 0, total: null, loading: false });
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const listRef = React.useRef(null);
  const PAGE = 30;
  const fetchPage = useCallback(async (reset) => {
    setList((prev) => ({ ...prev, loading: true }));
    try {
      const p2 = { limit: PAGE, ...(q ? { q } : {}), ...params };
      p2.offset = reset ? 0 : list.offset + PAGE;
      const d = shapedList((await api.get(endpoint, { params: p2 })).data);
      setList((prev) => ({ rows: reset ? d.rows : [...prev.rows, ...d.rows], offset: p2.offset,
        total: Number(d.counts?.total ?? d.rows.length), loading: false }));
    } catch (e) { setNote(e?.response?.data?.error || e.message); setList((prev) => ({ ...prev, loading: false })); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, JSON.stringify(params), list.offset]);
  useEffect(() => {
    const t = setTimeout(() => fetchPage(true), q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const onScroll = () => {
    const el = listRef.current;
    if (!el || list.loading || list.total == null || list.rows.length >= list.total) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) fetchPage(false);
  };
  const go = async () => {
    setBusy(true); setNote(null);
    try { await onPick(pick); onClose(); }
    catch (e) { setNote(e?.response?.data?.error || e.message); setBusy(false); }
  };
  return (
    <FormModal title={title} onClose={onClose}>
      <div className={s.modalForm}>
        <span className={s.f}><label>Search</label>
          <input value={q} onChange={(e) => setQ(e.target.value)} /></span>
      </div>
      <div className={s.pickList} ref={listRef} onScroll={onScroll}>
        {list.rows.map((r) => { const m = mapRow(r); return (
          <button key={r.id} type="button" className={`${s.pickRow} ${pick === r.id ? s.pickOn : ''}`}
                  onClick={() => setPick(r.id)}>
            <span className={s.radio} aria-hidden="true" />
            <span className={s.pickName}>{m.title}<small>{m.sub}</small></span>
            <span />
          </button>
        ); })}
        {list.loading && <div style={{ padding: 12 }}><Loading what="stock" /></div>}
        {!list.loading && !list.rows.length && <div style={{ padding: 14 }} className={s.hint}>Nothing eligible — add one on its own tab first.</div>}
      </div>
      <div className={s.pickFoot}>{list.total != null ? `${list.rows.length} of ${list.total} loaded — scroll for more` : ' '}</div>
      {note && <div className={s.badBanner}>{note}</div>}
      <div className={s.modalFoot}>
        <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
        <button type="button" className={s.btnCreate} disabled={!pick || busy} onClick={go}>{verb}</button>
      </div>
    </FormModal>
  );
}

const SIM_EDIT_FIELDS = [
  { key: 'sim_number', label: 'ICCID' },
  { key: 'provider', label: 'Provider' },
  { key: 'mobile_number', label: 'Mobile number' },
  { key: 'imsi_number', label: 'IMSI' },
  { key: 'plan_name', label: 'Plan name' },
  { key: 'monthly_cost_paise', label: 'Monthly cost (paise)', type: 'number',
    hint: 'The recorded REAL plan cost. Today\'s payout recovery bills the agreement\'s configured rate (₹170) — this field is the truth that lets you change that decree later.' },
  { key: 'notes', label: 'Notes', textarea: true },
];
const VEHICLE_EDIT_FIELDS = [
  { key: 'registration_number', label: 'Registration number' },
  { key: 'make', label: 'Make' },
  { key: 'model', label: 'Model' },
  { key: 'model_year', label: 'Model year', type: 'number' },
  { key: 'colour', label: 'Colour' },
  { key: 'fuel_type', label: 'Fuel type' },
  { key: 'chassis_number', label: 'Chassis number' },
  { key: 'body_type', label: 'Body type', hint: 'sedan / suv / hatchback / muv / van / other' },
  { key: 'rc_registered_on', label: 'RC — date of registration', type: 'date' },
  { key: 'rc_expires_on', label: 'RC expires on', type: 'date' },
  { key: 'insurance_expires_on', label: 'Insurance expires on', type: 'date' },
  { key: 'puc_expires_on', label: 'PUC expires on', type: 'date' },
  { key: 'fitness_expires_on', label: 'Fitness expires on', type: 'date' },
  { key: 'permit_expires_on', label: 'Permit expires on', type: 'date',
    hint: 'The compliance expiries drive the Vehicles registry facets and the premise slot warning.' },
  { key: 'notes', label: 'Notes', textarea: true },
];
const DRIVER_EDIT_FIELDS = [
  { key: 'driver_name', label: 'Name' },
  { key: 'driver_mobile', label: 'Mobile' },
];

// V346 person facts — audited person-keyed (driver_person_field_events),
// name/mobile edits keep riding the assignment path above
const DRIVER_PERSON_FIELDS = [
  { key: 'pan_number', label: 'PAN', hint: 'AAAAA9999A' },
  { key: 'work_city', label: 'Work city', hint: 'mumbai / pune / hyderabad / delhi / dubai — the DRIVER’s location, not the premise’s' },
  { key: 'permanent_address', label: 'Permanent address', textarea: true },
  { key: 'permanent_pincode', label: 'Permanent pincode' },
  { key: 'communication_address', label: 'Communication address', textarea: true },
  { key: 'communication_pincode', label: 'Communication pincode' },
  { key: 'dl_expires_on', label: 'Driving licence expires', type: 'date' },
  { key: 'permit_expires_on', label: 'Municipal permit expires', type: 'date',
    hint: 'The DRIVER’s professional municipal-corporation permit — the vehicle’s taxi permit lives on the vehicle.' },
  { key: 'notes', label: 'Notes', textarea: true },
];

/* ── the drawer: BM's details panel, translated ────────────────────────── */

function ActivityLog({ params }) {
  const key = JSON.stringify(params);
  const { data, error, loading, reload } = useApi(
    () => api.get('/v2/onboarding/activity', { params: JSON.parse(key) }).then((r) => {
      if (!r.data || !Array.isArray(r.data.rows)) throw new Error('The server did not answer with the action log.');
      return r.data.rows;
    }), [key]);
  return (
    <>
      {loading && <Loading what="the action log" />}
      {error && <ErrorState error={error} onRetry={reload} />}
      {data && !data.length && <p className={s.hint}>No recorded events yet.</p>}
      {data && data.length > 0 && (
        <div className={s.tl}>
          {data.map((e, i) => (
            <div key={i} className={s.tlItem}>
              <span className={s.tlWhen}>{fmtWhen(e.at)}</span>
              <span>
                <span className={s.tlWhat}>{e.what}</span>
                <span className={s.tlNode}> · {e.node}</span>
                {e.actor && <div className={s.tlActor}>by {e.actor}</div>}
              </span>
            </div>
          ))}
        </div>
      )}
      <p className={s.hint}>
        Only real history is shown: SIM allocations, vehicle assignments, driver assignments,
        ownership intervals and lifecycle changes are all dated records now. Lifecycle history
        begins at V335 — changes made before it landed are not replayable.
      </p>
    </>
  );
}

/* Review primitives — BM's ReviewCard / ReviewSection / IdLine / StatusPill,
   read from ReviewPrimitives.js and re-keyed to v2 tokens. Rows are
   label-over-value with hairlines; actions live in card FOOTERS. */

function RvCard({ title, head, footer, children }) {
  return (
    <section className={s.rvCard}>
      {(title || head) && (
        <div className={s.rvHead}><span className={s.rvTitle}>{title}</span>{head}</div>
      )}
      <div className={s.rvBody}>{children}</div>
      {footer && <div className={s.rvFoot}>{footer}</div>}
    </section>
  );
}

function RvRow({ label, hint, children }) {
  return (
    <div className={s.rvRow}>
      <div className={s.rvLabel}>{label}</div>
      <div className={s.rvValue}>{children ?? '—'}</div>
      {hint && <div className={s.rvHint}>{hint}</div>}
    </div>
  );
}

function IdLine({ id }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(t);
  }, [copied]);
  if (!id) return null;
  return (
    <span className={s.idLine}>
      {id}
      <button type="button" aria-label="Copy id"
              onClick={async () => { try { await navigator.clipboard.writeText(String(id)); setCopied(true); } catch (_) {} }}>
        {copied ? <CheckCircle2 size={12} style={{ color: 'var(--grow-text)' }} /> : <Copy size={12} />}
      </button>
    </span>
  );
}

const SP_TONE = { grow: 'spGrow', warn: 'spWarn', bad: 'spBad', dim: 'spDim', iris: 'spIris', pend: 'spPend' };
function StatusPill({ tone = 'dim', children }) {
  return <span className={`${s.statusPill} ${s[SP_TONE[tone]]}`}><i />{children}</span>;
}

const LIFECYCLE_SP = { onboarding: 'warn', deployed: 'grow', parked: 'warn', retired: 'bad' };

function DeploymentDrawer({ id, section: initialSection, onChanged, onClose }) {
  const [row, setRow] = useState(null);
  const [err, setErr] = useState(null);
  const [section, setSection] = useState(initialSection || 'overview');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const [pendingLifecycle, setPendingLifecycle] = useState(null);
  const [pmOpen, setPmOpen] = useState(null); // 'pick' | 'edit'
  const [dmOpen, setDmOpen] = useState(null); // 'pick' | 'edit'
  const [simPickOpen, setSimPickOpen] = useState(false);
  const [simEditOpen, setSimEditOpen] = useState(null); // full sims row once fetched
  const [vehPickOpen, setVehPickOpen] = useState(false);
  const [vehEditOpen, setVehEditOpen] = useState(null); // full vehicles row once fetched
  const [drvAddOpen, setDrvAddOpen] = useState(false);
  const [drvEditOpen, setDrvEditOpen] = useState(false);

  const fetchRow = useCallback(async () => {
    try {
      const d = shapedList((await api.get('/v2/onboarding/deployments', { params: { id, limit: 1 } })).data);
      if (!d.rows.length) throw new Error('This premise no longer exists.');
      setRow(d.rows[0]);
      setErr(null);
    } catch (e) { setErr(e?.response?.data?.error || e.message); }
  }, [id]);
  useEffect(() => { fetchRow(); }, [fetchRow]);


  const act = async (path, body) => {
    setBusy(true); setNote(null);
    try { await api.post(path, body); await fetchRow(); onChanged(); }
    catch (e) { setNote(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const missing = row ? (row.drift ? [] : missingOf(row)) : [];
  const rail = row ? [
    { key: 'overview', Icon: Boxes, label: 'premise' },
    ...SLOTS.filter((sl) => sl.applies(row)),
    { key: 'activity', Icon: History, label: 'action log' },
  ] : [];

  return (
    <Drawer onClose={onClose}
      title={row ? row.node_name : '…'}
      eyebrow="Premise"
      head={row && (
        <div className={s.statusRow}>
          <StatusPill tone={LIFECYCLE_SP[row.lifecycle_status]}>{row.lifecycle_status}</StatusPill>
          {row.drift
            ? <StatusPill tone="bad">operator drift</StatusPill>
            : deviceOut(row)
              ? <StatusPill tone="bad">device out of service</StatusPill>
              : missing.length
                ? <StatusPill tone="warn">needs {missing.join(', ')}</StatusPill>
                : <StatusPill tone="grow">ready</StatusPill>}
          <span className={s.hint}>{isCab(row) ? 'in-cab' : row.platform.replace(/_/g, ' ')} · {row.city}</span>
          <span className={s.spacer} />
          {LIFECYCLES.filter((l) => l !== row.lifecycle_status).map((l) => (
            <button key={l} type="button" className={s.chip} disabled={busy}
                    onClick={() => setPendingLifecycle(l)}>
              → {l}
            </button>
          ))}
        </div>
      )}
      footer={row && (
        <>
          <span className={s.hint}>Money and terms live on their own screens:</span>
          <NavLink to="/v2/payouts/ownership">Ownership</NavLink>
          <NavLink to="/v2/payouts/agreements">Agreements &amp; gates</NavLink>
          <NavLink to={`/v2/demand/tenants${row.money_tenant_id ? `?id=${row.money_tenant_id}` : ''}`}>Tenant entity</NavLink>
          <span className={s.spacer} />
          <button type="button" className={s.btnTool} onClick={onClose}>Close</button>
        </>
      )}>
      {row && simPickOpen && (
        <StockPickModal title="Choose SIM" endpoint="/v2/onboarding/sims" params={{ state: 'stock' }}
          mapRow={(r2) => ({ title: r2.sim_number, sub: `${r2.provider || 'provider unknown'} · ${inrP(r2.monthly_cost_paise)}/mo` })}
          verb="Allocate here"
          onPick={async (sid) => { await api.post(`/v2/onboarding/sims/${sid}/allocate`, { edge_node_id: row.id }); await fetchRow(); onChanged(); }}
          onClose={() => setSimPickOpen(false)} />
      )}
      {row && simEditOpen && (
        <EntityEditModal title={`SIM ${simEditOpen.sim_number}`} endpoint={`/v2/onboarding/sims/${simEditOpen.id}/edit`}
          fields={SIM_EDIT_FIELDS}
          photoFields={[
            { key: 'card_image_key', label: 'SIM card photo', endpoint: '/v2/onboarding/sims/upload-url', kind: 'card', existingKey: simEditOpen.card_image_key },
            { key: 'envelope_image_key', label: 'Envelope photo (PIN/PUK)', endpoint: '/v2/onboarding/sims/upload-url', kind: 'envelope', existingKey: simEditOpen.envelope_image_key },
          ]}
          initial={{ sim_number: simEditOpen.sim_number, provider: simEditOpen.provider, mobile_number: simEditOpen.mobile_number,
            imsi_number: simEditOpen.imsi_number, plan_name: simEditOpen.plan_name,
            monthly_cost_paise: simEditOpen.monthly_cost_paise, notes: simEditOpen.notes }}
          onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setSimEditOpen(null)} />
      )}
      {row && vehPickOpen && (
        <StockPickModal title="Choose vehicle" endpoint="/v2/onboarding/vehicles" params={{ state: 'unlinked' }}
          mapRow={(r2) => ({ title: r2.registration_number, sub: [r2.make, r2.model].filter(Boolean).join(' ') || 'make unknown' })}
          verb="Link here"
          onPick={async (vid) => { await api.post(`/v2/onboarding/premises/${row.id}/vehicle`, { vehicle_id: vid }); await fetchRow(); onChanged(); }}
          onClose={() => setVehPickOpen(false)} />
      )}
      {row && vehEditOpen && (
        <EntityEditModal title={`Vehicle ${vehEditOpen.registration_number}`} endpoint={`/v2/onboarding/vehicles/${vehEditOpen.id}/edit`}
          fields={VEHICLE_EDIT_FIELDS}
          initial={{ registration_number: vehEditOpen.registration_number, make: vehEditOpen.make, model: vehEditOpen.model,
            model_year: vehEditOpen.model_year, colour: vehEditOpen.colour, fuel_type: vehEditOpen.fuel_type,
            chassis_number: vehEditOpen.chassis_number, body_type: vehEditOpen.body_type,
            rc_registered_on: vehEditOpen.rc_registered_on, notes: vehEditOpen.notes,
            rc_expires_on: vehEditOpen.rc_expires_on, insurance_expires_on: vehEditOpen.insurance_expires_on,
            puc_expires_on: vehEditOpen.puc_expires_on, fitness_expires_on: vehEditOpen.fitness_expires_on,
            permit_expires_on: vehEditOpen.permit_expires_on }}
          onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setVehEditOpen(null)} />
      )}
      {row && drvAddOpen && (
        <DriverIntake fixedPremise={{ id: row.id, name: row.node_name }}
          onDone={() => { fetchRow(); onChanged(); }} onClose={() => setDrvAddOpen(false)} />
      )}
      {row && drvEditOpen && row.driver_assignment_id && (
        <EntityEditModal title={`Driver ${row.driver_name}`} endpoint={`/v2/onboarding/drivers/${row.driver_assignment_id}/edit`}
          fields={DRIVER_EDIT_FIELDS}
          initial={{ driver_name: row.driver_name, driver_mobile: row.driver_mobile }}
          onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setDrvEditOpen(false)} />
      )}
      {row && dmOpen && (
        <DeviceModal verb="Install here" eligibleParams={{ installable: 1 }} initialMode={dmOpen}
          currentDeviceId={row.screen_id || null}
          onMap={async (did) => { await api.post(`/v2/onboarding/premises/${row.id}/install-device`, { device_id: did }); await fetchRow(); onChanged(); }}
          onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setDmOpen(null)} />
      )}
      {row && pmOpen && (
        <PremiseModal initialMode="edit" currentPremiseId={row.id} verb={null} eligibleParams={null}
          onMap={null} onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setPmOpen(null)} />
      )}
      {row && pendingLifecycle && (
        <ConfirmModal
          title={`Move ${row.node_name} to ${pendingLifecycle}?`}
          body={LIFECYCLE_CONSEQUENCE(row, pendingLifecycle)}
          confirmLabel={`Move to ${pendingLifecycle}`}
          danger={pendingLifecycle === 'retired'}
          onCancel={() => setPendingLifecycle(null)}
          onConfirm={() => { const l = pendingLifecycle; setPendingLifecycle(null); act(`/v2/onboarding/premises/${row.id}/lifecycle`, { status: l }); }} />
      )}
      {err && <div style={{ padding: 'var(--s4)' }}><ErrorState error={err} onRetry={fetchRow} /></div>}
      {!err && !row && <div style={{ padding: 'var(--s4)' }}><Loading what="the premise" /></div>}
      {row && (
        <div className={s.canvas}>
          <nav className={s.rail} aria-label="Premise slots">
            {rail.map((sl) => (
              <button key={sl.key} type="button"
                      className={`${s.railItem} ${section === sl.key ? s.railOn : ''}`}
                      onClick={() => setSection(sl.key)}>
                <span className={`${s.railLetter} ${sl.state ? segCls(sl.state(row)) : s.segDim}`}>
                  <sl.Icon size={12} aria-hidden="true" />
                </span>
                {sl.label}
                {sl.state && sl.state(row) === 'miss' && <span className={s.halfMoon} title="Not configured yet" aria-label="Not configured yet" />}
                {sl.state && sl.state(row) === 'house' && <span className={s.halfMoonWarn} title="Registry tag only — earnings stay house money" aria-label="House money" />}
                {sl.state && sl.state(row) === 'drift' && <span className={s.fullMoon} title="Drift — needs attention" aria-label="Drift" />}
              </button>
            ))}
          </nav>
          <div className={s.paneScroll}>
            {note && <div className={s.badBanner}>{note}</div>}

            {section === 'overview' && (
              <>
                <RvCard title="Premise"
                  footer={<button type="button" className={s.btnTool} onClick={() => setPmOpen('edit')}>Edit premise…</button>}>
                  <RvRow label="Name" hint="Born the day the location is signed — the plate for a cab, the venue for DOOH.">
                    {row.node_name}
                    <div><IdLine id={row.id} /></div>
                  </RvRow>
                  <RvRow label="Status">
                    <span className={s.statusRow}>
                      <StatusPill tone={LIFECYCLE_SP[row.lifecycle_status]}>{row.lifecycle_status}</StatusPill>
                      {row.drift ? <StatusPill tone="bad">operator drift</StatusPill>
                        : deviceOut(row) ? <StatusPill tone="bad">device out of service</StatusPill>
                          : missing.length ? <StatusPill tone="warn">needs {missing.join(', ')}</StatusPill>
                            : <StatusPill tone="grow">ready</StatusPill>}
                    </span>
                  </RvRow>
                  {row.on_track != null && (
                    <RvRow label="Today's gate"
                           hint="Advisory, not money — the eligibility freezer judges the finished day with this same gate.">
                      <span title="refreshed hourly">{gateLine(row)}</span>
                    </RvRow>
                  )}
                  <RvRow label="Premise type">{isCab(row) ? 'in-cab' : row.platform.replace(/_/g, ' ')}</RvRow>
                  <RvRow label="City">{row.city}</RvRow>
                  <RvRow label="Operator" hint="The registry tag — money follows ownership history, not this label.">
                    {row.operator || <small>none — house</small>}
                  </RvRow>
                  <RvRow label="Created">{fmtDate(row.created_at)}</RvRow>
                </RvCard>
                <RvCard title="Assembly">
                  <div className={s.rvRow}>
                    <div className={s.bigTrack}>
                      {SLOTS.filter((sl) => sl.applies(row)).map((sl) => (
                        <span key={sl.key} className={`${s.seg} ${segCls(sl.state(row))}`} title={sl.tip(row)}
                              role="img" aria-label={`${sl.label}: ${sl.tip(row)}`}>
                          <sl.Icon size={14} aria-hidden="true" />
                        </span>
                      ))}
                    </div>
                  </div>
                  {SLOTS.filter((sl) => sl.applies(row)).map((sl) => (
                    <RvRow key={sl.key} label={sl.label[0].toUpperCase() + sl.label.slice(1)}>
                      <span style={{ cursor: 'pointer' }} role="button" tabIndex={0}
                            onClick={() => setSection(sl.key)}
                            onKeyDown={(e) => { if (e.key === 'Enter') setSection(sl.key); }}>
                        {sl.tip(row)}
                      </span>
                    </RvRow>
                  ))}
                </RvCard>
              </>
            )}

            {section === 'vehicle' && (
              <RvCard title="Vehicle"
                footer={row.vehicle_id ? (
                  <>
                    <button type="button" className={s.btnTool} disabled={busy}
                            onClick={() => act(`/v2/onboarding/premises/${row.id}/vehicle`, { vehicle_id: null })}>
                      Unlink vehicle
                    </button>
                    <span className={s.spacer} />
                    <button type="button" className={s.btnTool}
                            onClick={async () => {
                              const d = shapedList((await api.get('/v2/onboarding/vehicles', { params: { id: row.vehicle_id, limit: 1 } })).data);
                              if (d.rows.length) setVehEditOpen(d.rows[0]);
                            }}>Edit vehicle…</button>
                  </>
                ) : <button type="button" className={s.btnCreate} onClick={() => setVehPickOpen(true)}>Choose vehicle…</button>}>
                {row.vehicle_id ? (
                  <>
                    <RvRow label="Registration">{row.registration_number}</RvRow>
                    <RvRow label="Make">{[row.make, row.model].filter(Boolean).join(' ') || <small>unknown</small>}</RvRow>
                    <RvRow label="Nearest document expiry"
                           hint="Across RC · insurance · PUC · fitness · permit — compliance detail lives on the Vehicles tab.">
                      {row.nearest_doc_expiry ? fmtDate(row.nearest_doc_expiry) : <small>none recorded</small>}
                    </RvRow>
                  </>
                ) : (
                  <RvRow label="No vehicle linked"
                         hint={isCab(row) ? 'An in-cab premise needs one before it counts as ready.' : 'Not required for this premise type.'}>
                    <small>The link is honest — nothing is guessed from names.</small>
                  </RvRow>
                )}
              </RvCard>
            )}

            {section === 'screen' && (
              <RvCard title="Device"
                footer={row.screen_id ? (
                  <>
                    <button type="button" className={s.btnTool} disabled={busy}
                            onClick={() => act(`/v2/onboarding/premises/${row.id}/remove-device`, {})}>
                      Remove device (back to shelf)
                    </button>
                    <span className={s.spacer} />
                    <button type="button" className={s.btnTool} onClick={() => setDmOpen('edit')}>Edit device…</button>
                  </>
                ) : <button type="button" className={s.btnCreate} onClick={() => setDmOpen('pick')}>Choose device…</button>}>
                {row.screen_id ? (
                  <>
                    <RvRow label="Device" hint="Paired through the hardware bridge — the same identifiers frozen on every billed play.">
                      {row.thing_name}
                      <div><IdLine id={row.screen_id} /></div>
                    </RvRow>
                    <RvRow label="Model">{row.device_model || <small>unknown</small>}</RvRow>
                    <RvRow label="IMEI"><span className={s.rvMono}>{row.device_imei_number || '—'}</span></RvRow>
                    <RvRow label="Serial number"><span className={s.rvMono}>{row.device_serial_number || '—'}</span></RvRow>
                    <RvRow label="Widevine id"><span className={s.rvMono}>{row.device_widevine_id || '—'}</span></RvRow>
                    {deviceOut(row) && (
                      <RvRow label="Out of service"
                             hint="V343 lifecycle — serving is off for this device wherever it rides; this is its cause, not mere offline-ness. Reactivate or swap it from the Devices tab.">
                        <StatusPill tone="bad">{row.device_lifecycle} — serving off</StatusPill>
                      </RvRow>
                    )}
                    <RvRow label="Liveness" hint="Heard = the freshest exchange (telemetry ≈ 1.5 min while alive); the flag can lag it.">
                      <LiveDot status={row.connection_status} at={row.last_heard_at} />
                      heard {relTime(row.last_heard_at)} <small>· flag: {row.connection_status || 'unknown'}</small>
                    </RvRow>
                    <RvRow label="Device owner" hint="Earns the device share of this device's plays.">
                      {row.device_owner || <small>none — house money</small>}
                    </RvRow>
                  </>
                ) : (
                  <RvRow label="No device installed"
                         hint="Installing copies the device's identifiers here (never typed by hand), clears any previous premise's copy, and logs the move. A procured device's own first boot completes the pairing.">
                    <small>Pick one from the shelf below.</small>
                  </RvRow>
                )}
              </RvCard>
            )}

            {section === 'sim' && (
              <RvCard title="SIM"
                footer={row.sim_id ? (
                  <>
                    <button type="button" className={s.btnTool} disabled={busy}
                            onClick={() => act(`/v2/onboarding/sims/${row.sim_id}/deallocate`, {})}>
                      Return to stock
                    </button>
                    <span className={s.spacer} />
                    <button type="button" className={s.btnTool}
                            onClick={async () => {
                              const d = shapedList((await api.get('/v2/onboarding/sims', { params: { id: row.sim_id, limit: 1 } })).data);
                              if (d.rows.length) setSimEditOpen(d.rows[0]);
                            }}>Edit SIM…</button>
                  </>
                ) : <button type="button" className={s.btnCreate} onClick={() => setSimPickOpen(true)}>Choose SIM…</button>}>
                {row.sim_id ? (
                  <>
                    <RvRow label="ICCID">{row.sim_number}</RvRow>
                    <RvRow label="Provider">{row.provider || <small>unknown</small>}</RvRow>
                    <RvRow label="Monthly cost" hint="Recovers from the premise owner at payout close, before TDS.">
                      {inrP(row.monthly_cost_paise)}/month
                    </RvRow>
                  </>
                ) : (
                  <RvRow label="No SIM allocated"
                         hint="Allocations are dated — history accumulates, nothing is overwritten.">
                    <small>Pick one from stock below.</small>
                  </RvRow>
                )}
              </RvCard>
            )}

            {section === 'driver' && (
              <RvCard title="Driver"
                footer={row.driver_name ? (
                  <>
                    <button type="button" className={s.btnTool} disabled={busy}
                            onClick={() => act(`/v2/onboarding/drivers/${row.driver_assignment_id}/unassign`, {})}>
                      Unassign
                    </button>
                    <span className={s.spacer} />
                    <button type="button" className={s.btnTool} onClick={() => setDrvEditOpen(true)}>Edit driver…</button>
                  </>
                ) : <button type="button" className={s.btnCreate} onClick={() => setDrvAddOpen(true)}>Assign driver…</button>}>
                {row.driver_name ? (
                  <>
                    <RvRow label="Active driver">{row.driver_name}</RvRow>
                    <RvRow label="Since">{fmtDate(row.driver_since)}</RvRow>
                    <RvRow label="Role in revenue" hint="Advisory only — the gate runs on trips and driving hours, not names.">
                      <small>Does not gate money.</small>
                    </RvRow>
                  </>
                ) : (
                  <RvRow label="No active driver" hint="Advisory, not a gate — dated history shows in the action log.">
                    <small>Assign one in classic Drivers &amp; vehicles.</small>
                  </RvRow>
                )}
              </RvCard>
            )}

            {section === 'operator' && (
              <RvCard title="Ownership"
                footer={<NavLink to="/v2/payouts/ownership" className={s.hint}>Transfer ownership on Payouts › Ownership →</NavLink>}>
                <RvRow label="Premise owner" hint="Earns the premise share of this premise's plays.">
                  {row.money_operator || <small>none — earnings stay house money</small>}
                </RvRow>
                <RvRow label="Device owner" hint="Earns the device share of the device riding here.">
                  {row.device_owner || <small>{row.screen_id ? 'none — house money' : 'no device here yet'}</small>}
                </RvRow>
                <RvRow label="Registry tag" hint="An ops label; money follows ownership history, never this.">
                  {row.registry_operator || <small>none</small>}
                </RvRow>
                {row.drift && (
                  <div className={s.rvRow}>
                    <div className={s.badBanner}>DRIFT: the registry tag and the money-plane owner disagree.
                      Fix the tag or transfer ownership.</div>
                  </div>
                )}
              </RvCard>
            )}

            {section === 'terms' && (
              <RvCard title="Terms"
                footer={<NavLink to="/v2/payouts/agreements" className={s.hint}>Rates, gates and slabs on Payouts › Agreements →</NavLink>}>
                <RvRow label="Tenant TnC">
                  {row.operator_acceptances > 0 ? `accepted (${row.operator_acceptances} on record)` : <small>none on record — advisory</small>}
                </RvRow>
                <RvRow label="Governing agreement" hint="Fleet-anchored: this premise's owner's own agreement when one exists, else the defaults.">
                  {row.has_own_terms ? 'own agreement' : 'platform defaults'}
                </RvRow>
              </RvCard>
            )}

            {section === 'activity' && (
              <RvCard title="Action log">
                <div className={s.rvRow}>
                  <ActivityLog params={{ node_ids: row.id }} />
                </div>
              </RvCard>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}

/* ═══ ONE panel anatomy for every entity ════════════════════════════════
   Founder, 6 Sep: every right panel must be the SAME cognitive shape —
   eyebrow · title · status pills · left rail with moons · carded canvas ·
   sticky footer — and every relationship must be assignable from EITHER
   side. EntityDrawer is that shape; Premise, Device, SIM, Vehicle and
   Driver drawers are configurations of it, never reinventions. */

function EntityDrawer({ eyebrow, title, pills, footerNote, sections, defaultSection, onClose, children }) {
  const [section, setSection] = useState(defaultSection || (sections[0] && sections[0].key));
  const active = sections.find((x) => x.key === section) || sections[0];
  return (
    <Drawer onClose={onClose} eyebrow={eyebrow} title={title}
      head={pills && <div className={s.statusRow}>{pills}</div>}
      footer={<>
        {footerNote && <span className={s.hint}>{footerNote}</span>}
        <span className={s.spacer} />
        <button type="button" className={s.btnTool} onClick={onClose}>Close</button>
      </>}>
      {children}
      <div className={s.canvas}>
        <nav className={s.rail} aria-label={`${eyebrow} sections`}>
          {sections.map((sec) => (
            <button key={sec.key} type="button"
                    className={`${s.railItem} ${section === sec.key ? s.railOn : ''}`}
                    onClick={() => setSection(sec.key)}>
              <span className={`${s.railLetter} ${segCls(sec.state || 'na')}`}>
                <sec.Icon size={12} aria-hidden="true" />
              </span>
              {sec.label}
              {sec.state === 'miss' && <span className={s.halfMoon} title="Not configured yet" />}
              {sec.state === 'house' && <span className={s.halfMoonWarn} title="Registry tag only — house money" />}
              {sec.state === 'drift' && <span className={s.fullMoon} title="Drift — needs attention" />}
            </button>
          ))}
        </nav>
        <div className={s.paneScroll}>{active && active.render()}</div>
      </div>
    </Drawer>
  );
}

/* ── the PREMISE MODAL: the panel's relationship editor, piloted here and
   to be repeated for every rail option. Three modes in one dialog:
     pick  — see enough to DECIDE (search, type · city, status, readiness)
     add   — create a premise AND map it, one flow
     edit  — change the mapped premise's facts, every field audited (V339)
   ─────────────────────────────────────────────────────────────────────── */

function PremiseModal({ verb, eligibleParams, currentPremiseId, onMap, onChanged, onClose, initialMode }) {
  const [mode, setMode] = useState(initialMode || (currentPremiseId && !verb ? 'edit' : 'pick'));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [confirm, setConfirm] = useState(null); // {title, body, onYes}

  // ── pick: server-filtered, server-paginated (30 a page, infinite scroll) ──
  const [flt, setFlt] = useState({ q: '', type: '', status: '', city: '', owner: '' });
  const [list, setList] = useState({ rows: [], offset: 0, total: null, loading: false });
  const [pick, setPick] = useState('');
  const [owners, setOwners] = useState(null);
  const listRef = React.useRef(null);
  const PAGE = 30;

  const fetchPage = useCallback(async (reset) => {
    setList((prev) => ({ ...prev, loading: true }));
    try {
      const params = {
        limit: PAGE, offset: reset ? 0 : undefined,
        ...(flt.q ? { q: flt.q } : {}), ...(flt.type ? { type: flt.type } : {}),
        ...(flt.status ? { lifecycle: flt.status } : {}), ...(flt.city ? { city: flt.city } : {}),
        ...(flt.owner ? { operator: flt.owner } : {}), ...(eligibleParams || {}),
      };
      if (!reset) params.offset = list.offset + PAGE;
      const d = shapedList((await api.get('/v2/onboarding/deployments', { params })).data);
      setList((prev) => ({
        rows: reset ? d.rows : [...prev.rows, ...d.rows],
        offset: params.offset || 0,
        total: Number(d.counts?.total ?? d.rows.length),
        loading: false,
      }));
    } catch (e) {
      setNote(e?.response?.data?.error || e.message);
      setList((prev) => ({ ...prev, loading: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flt, eligibleParams && JSON.stringify(eligibleParams), list.offset]);

  useEffect(() => {
    if (mode !== 'pick') return undefined;
    const t = setTimeout(() => fetchPage(true), flt.q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, flt]);
  useEffect(() => {
    api.get('/v2/onboarding/premise-owners').then((r) => setOwners(r.data?.rows || [])).catch(() => setOwners([]));
  }, []);
  const onListScroll = () => {
    const el = listRef.current;
    if (!el || list.loading || list.total == null || list.rows.length >= list.total) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) fetchPage(false);
  };

  // ── add / edit fields ──
  const [add, setAdd] = useState({ node_name: '', platform: 'in_cab', city: 'mumbai', tenant_id: '' });
  const [edit, setEdit] = useState(null);
  const [tenants, setTenants] = useState(null);
  useEffect(() => {
    api.get('/v2/tenants').then((r) => setTenants((r.data?.tenants || []).filter((t) => t.is_active !== false)))
      .catch(() => setTenants([]));
  }, []);
  useEffect(() => {
    if (mode === 'edit' && currentPremiseId && edit == null) {
      api.get('/v2/onboarding/deployments', { params: { id: currentPremiseId, limit: 1 } })
        .then((r) => { const c = shapedList(r.data).rows[0]; if (c) setEdit({ node_name: c.node_name, platform: c.platform, city: c.city, tenant_id: '', _registry: c.registry_operator, _was: c }); });
    }
  }, [mode, currentPremiseId, edit]);

  const doMap = async (premiseId) => {
    setBusy(true); setNote(null);
    try { await onMap(premiseId); onClose(); }
    catch (e) { setNote(e?.response?.data?.error || e.message); setBusy(false); }
  };

  // ── consequence confirms: say what changes BEFORE it changes ──
  const confirmAdd = () => {
    const t = TYPE_OPTS.find((o) => o.value === add.platform)?.label;
    const op = (tenants || []).find((x) => x.id === add.tenant_id)?.name;
    setConfirm({
      title: verb ? `Create & ${verb.toLowerCase()}?` : 'Create this premise?',
      body: <>
        <p style={{ margin: '0 0 8px' }}>Creates <b>{add.node_name}</b> — {t} · {add.city} · operator {op || 'none (house)'}
          {verb ? <> — and immediately <b>{verb.toLowerCase()}</b> for this asset (the mapping is dated and audited).</> : '.'}</p>
        <p style={{ margin: 0 }}>The operator field is the registry tag: money follows ownership history
          on Payouts › Ownership, never this label.</p>
      </>,
      label: verb ? `Create & ${verb.toLowerCase()}` : 'Create premise',
      onYes: async () => {
        setBusy(true); setNote(null);
        try {
          const created = (await api.post('/v2/onboarding/premises', {
            node_name: add.node_name, platform: add.platform, city: add.city, tenant_id: add.tenant_id || null,
          })).data;
          if (verb && onMap) { await onMap(created.id); } else { onChanged(); }
          onClose();
        } catch (e) { setNote(e?.response?.data?.error || e.message); setBusy(false); setConfirm(null); }
      },
    });
  };

  const confirmEdit = () => {
    const was = edit._was;
    const bits = [];
    if (edit.node_name !== was.node_name) {
      bits.push(<p key="n" style={{ margin: '0 0 8px' }}>Rename <b>{was.node_name}</b> → <b>{edit.node_name}</b>.
        Display-only: money rides the premise id, which never changes.</p>);
    }
    if (edit.platform !== was.platform) {
      const from = TYPE_OPTS.find((o) => o.value === was.platform)?.label;
      const to = TYPE_OPTS.find((o) => o.value === edit.platform)?.label;
      bits.push(<p key="t" style={{ margin: '0 0 8px' }}>Type <b>{from}</b> → <b>{to}</b>.{' '}
        {was.platform === 'in_cab'
          ? <>The <b>vehicle and driver slots stop applying</b> — their links stay recorded (dormant) and reappear if the type returns to in-cab. Readiness recomputes without them.</>
          : edit.platform === 'in_cab'
            ? <>This premise <b>gains vehicle and driver slots</b> and will need a vehicle before it counts as ready.</>
            : 'Slot requirements are unchanged.'}
      </p>);
    }
    if (edit.city !== was.city) {
      bits.push(<p key="c" style={{ margin: '0 0 8px' }}>City <b>{was.city}</b> → <b>{edit.city}</b> — labels and reporting only.</p>);
    }
    if (edit.tenant_id && edit.tenant_id !== '__house') {
      const nm = (tenants || []).find((x) => x.id === edit.tenant_id)?.name;
      bits.push(<p key="o" style={{ margin: '0 0 8px' }}>Operator tag → <b>{nm}</b>. <b>This does NOT move money.</b>{' '}
        Cashflows follow ownership history (Payouts › Ownership); if the tag disagrees with the money owner, DRIFT will flag until reconciled.</p>);
    }
    if (edit.tenant_id === '__house') {
      bits.push(<p key="o" style={{ margin: '0 0 8px' }}>Operator tag → <b>none (house)</b>. Money is untouched — the tag is a registry label.</p>);
    }
    if (!bits.length) { setNote('Nothing changed.'); return; }
    bits.push(<p key="a" style={{ margin: 0 }}>Every changed field is logged — old value, new value, your name.</p>);
    setConfirm({
      title: `Save changes to ${was.node_name}?`, body: <>{bits}</>, label: 'Save changes',
      onYes: async () => {
        setBusy(true); setNote(null);
        try {
          const body = { node_name: edit.node_name, platform: edit.platform, city: edit.city };
          if (edit.tenant_id === '__house') body.tenant_id = null;
          else if (edit.tenant_id) body.tenant_id = edit.tenant_id;
          await api.post(`/v2/onboarding/premises/${currentPremiseId}/edit`, body);
          onChanged(); onClose();
        } catch (e) { setNote(e?.response?.data?.error || e.message); setBusy(false); setConfirm(null); }
      },
    });
  };

  const setA = (k) => (v) => setAdd({ ...add, [k]: v });
  const setE = (k) => (v) => setEdit({ ...edit, [k]: v });
  const ownerOpts = [{ value: '', label: 'any owner' },
    ...((owners || []).map((o) => ({ value: o.name, label: o.name })))];
  const tenantOpts = (extra) => [...extra, ...((tenants || []).map((t) => ({ value: t.id, label: t.name, hint: t.is_personal ? 'personal' : undefined })))];

  return (
    <FormModal title="Premise" onClose={onClose}>
      {confirm && (
        <ConfirmModal title={confirm.title} body={confirm.body} confirmLabel={confirm.label}
                      onCancel={() => setConfirm(null)} onConfirm={confirm.onYes} />
      )}
      <div className={s.segCtl} role="tablist">
        {verb && <button type="button" role="tab" aria-selected={mode === 'pick'}
                className={`${s.segBtn} ${mode === 'pick' ? s.segOn : ''}`} onClick={() => setMode('pick')}>Pick existing</button>}
        <button type="button" role="tab" aria-selected={mode === 'add'}
                className={`${s.segBtn} ${mode === 'add' ? s.segOn : ''}`} onClick={() => setMode('add')}>Add new</button>
        <button type="button" role="tab" aria-selected={mode === 'edit'} disabled={!currentPremiseId}
                title={currentPremiseId ? undefined : 'No premise is mapped yet'}
                className={`${s.segBtn} ${mode === 'edit' ? s.segOn : ''}`} onClick={() => currentPremiseId && setMode('edit')}>Edit current</button>
      </div>

      {mode === 'pick' && (
        <>
          <div className={s.modalForm}>
            <span className={s.f}><label>Search</label>
              <input value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} placeholder="plate or venue…" /></span>
            <div className={s.filterGrid}>
              <span className={s.f}><label>Type</label>
                <Listbox value={flt.type} onChange={(v) => setFlt({ ...flt, type: v })}
                         options={[{ value: '', label: 'any type' }, ...TYPE_OPTS]} ariaLabel="Filter by premise type" /></span>
              <span className={s.f}><label>Status</label>
                <Listbox value={flt.status} onChange={(v) => setFlt({ ...flt, status: v })}
                         options={[{ value: '', label: 'any status' }, ...STATUS_OPTS]} ariaLabel="Filter by lifecycle" /></span>
              <span className={s.f}><label>City</label>
                <Listbox value={flt.city} onChange={(v) => setFlt({ ...flt, city: v })}
                         options={[{ value: '', label: 'any city' }, ...CITY_OPTS]} ariaLabel="Filter by city" /></span>
              <span className={s.f}><label>Premise owner</label>
                <Listbox value={flt.owner} onChange={(v) => setFlt({ ...flt, owner: v })}
                         options={ownerOpts} ariaLabel="Filter by premise owner" /></span>
            </div>
          </div>
          <div className={s.pickList} ref={listRef} onScroll={onListScroll}>
            {list.rows.map((n) => (
              <button key={n.id} type="button"
                      className={`${s.pickRow} ${pick === n.id ? s.pickOn : ''}`}
                      onClick={() => setPick(n.id)}>
                <span className={s.radio} aria-hidden="true" />
                <span className={s.pickName}>
                  {n.node_name}
                  <small>{isCab(n) ? 'in-cab' : n.platform.replace(/_/g, ' ')} · {n.city} · {n.operator || 'no operator'}</small>
                </span>
                <StatusPill tone={LIFECYCLE_SP[n.lifecycle_status]}>{n.lifecycle_status}</StatusPill>
              </button>
            ))}
            {list.loading && <div style={{ padding: 12 }}><Loading what="premises" /></div>}
            {!list.loading && !list.rows.length && <div style={{ padding: 14 }} className={s.hint}>No eligible premise matches these filters.</div>}
          </div>
          <div className={s.pickFoot}>
            {list.total != null ? `${list.rows.length} of ${list.total} eligible premises loaded — scroll for more` : ' '}
          </div>
          {note && <div className={s.badBanner}>{note}</div>}
          <div className={s.modalFoot}>
            <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
            <button type="button" className={s.btnCreate} disabled={!pick || busy} onClick={() => doMap(pick)}>{verb}</button>
          </div>
        </>
      )}

      {mode === 'add' && (
        <>
          <div className={s.modalForm}>
            <span className={s.f}><label>Name — the plate for a cab, the venue for DOOH</label>
              <input value={add.node_name} onChange={(e) => setAdd({ ...add, node_name: e.target.value })}
                     placeholder="Taxi Pune MH12AB1234 · Dadar Station TV 3" /></span>
            <div className={s.twoCol}>
              <span className={s.f}><label>Premise type</label>
                <Listbox value={add.platform} onChange={setA('platform')} options={TYPE_OPTS} ariaLabel="Premise type" /></span>
              <span className={s.f}><label>City</label>
                <Listbox value={add.city} onChange={setA('city')} options={CITY_OPTS} ariaLabel="City" /></span>
            </div>
            <span className={s.f}><label>Operator (registry tag) — any tenant</label>
              <Listbox value={add.tenant_id} onChange={setA('tenant_id')}
                       options={tenantOpts([{ value: '', label: 'none — house' }])} ariaLabel="Operator" /></span>
          </div>
          {!verb && <p className={s.hint} style={{ margin: 0 }}>This creates a SEPARATE premise — nothing here
            is re-mapped. To move an asset onto a new premise, open that asset&rsquo;s panel and use
            &lsquo;Choose premise… → Add new&rsquo;.</p>}
          {note && <div className={s.badBanner}>{note}</div>}
          <div className={s.modalFoot}>
            <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
            <button type="button" className={s.btnCreate} disabled={!add.node_name.trim() || busy} onClick={confirmAdd}>
              <Plus size={14} aria-hidden="true" /> {verb ? `Create & ${verb.toLowerCase()}` : 'Create premise'}
            </button>
          </div>
        </>
      )}

      {mode === 'edit' && (
        edit == null ? <Loading what="the premise" /> : (
          <>
            <div className={s.modalForm}>
              <span className={s.f}><label>Name</label>
                <input value={edit.node_name} onChange={(e) => setEdit({ ...edit, node_name: e.target.value })} /></span>
              <div className={s.twoCol}>
                <span className={s.f}><label>Premise type</label>
                  <Listbox value={edit.platform} onChange={setE('platform')} options={TYPE_OPTS} ariaLabel="Premise type" /></span>
                <span className={s.f}><label>City</label>
                  <Listbox value={edit.city} onChange={setE('city')} options={CITY_OPTS} ariaLabel="City" /></span>
              </div>
              <span className={s.f}><label>Operator (registry tag) — currently {edit._registry || 'none'}</label>
                <Listbox value={edit.tenant_id} onChange={setE('tenant_id')}
                         options={tenantOpts([{ value: '', label: 'keep as is' }, { value: '__house', label: 'none — revert to house' }])}
                         ariaLabel="Operator" /></span>
            </div>
            <p className={s.hint} style={{ margin: 0 }}>You&rsquo;ll see exactly what changes — and its consequences —
              before anything is saved. Every changed field is logged.</p>
            {note && <div className={s.badBanner}>{note}</div>}
            <div className={s.modalFoot}>
              <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
              <button type="button" className={s.btnCreate} disabled={busy} onClick={confirmEdit}>Save changes</button>
            </div>
          </>
        )
      )}
    </FormModal>
  );
}

function useEntity(path, id, onChangedOuter) {
  const [row, setRow] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const fetchRow = useCallback(async () => {
    try {
      const d = shapedList((await api.get(path, { params: { id, limit: 1 } })).data);
      if (!d.rows.length) throw new Error('This record no longer exists.');
      setRow(d.rows[0]);
      setErr(null);
    } catch (e) { setErr(e?.response?.data?.error || e.message); }
  }, [path, id]);
  useEffect(() => { fetchRow(); }, [fetchRow]);
  const act = async (p, body, after) => {
    setBusy(true); setNote(null);
    try { const r = await api.post(p, body); await fetchRow(); onChangedOuter(); if (after) after(r.data); }
    catch (e) { setNote(e?.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };
  return { row, err, busy, note, act, fetchRow, setNote };
}

function usePremises(filter, deps) {
  const [premises, setPremises] = useState(null);
  useEffect(() => {
    setPremises(null);
    api.get('/v2/onboarding/deployments', { params: { limit: 200 } })
      .then((r) => setPremises(shapedList(r.data).rows.filter(filter)))
      .catch(() => setPremises([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return premises;
}

function EntityLoading({ err, retry }) {
  return err
    ? <div style={{ padding: 'var(--s4)' }}><ErrorState error={err} onRetry={retry} /></div>
    : <div style={{ padding: 'var(--s4)' }}><Loading what="the record" /></div>;
}

function PhotoRow({ label, docKey, hint }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    if (docKey) {
      api.get('/v2/onboarding/doc-url', { params: { key: docKey } })
        .then((r) => { if (live) setUrl(r.data.url); }).catch(() => {});
    }
    return () => { live = false; };
  }, [docKey]);
  return (
    <RvRow label={label} hint={hint}>
      {docKey
        ? (url
            ? <a href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={label} className={s.photoThumb} />
              </a>
            : <small>loading photo…</small>)
        : <small>none on record</small>}
    </RvRow>
  );
}

/* ── the DEVICE MODAL — the premise editor's sibling: pick (filters +
   infinite scroll, eligibility in SQL) · add (create, own, photograph,
   and map in one flow) · edit (model/photos always; hardware identifiers
   only while procured — locked facts after first boot; every field
   audited via V340) ────────────────────────────────────────────────── */

const DEV_STATE_OPTS = [
  { value: '', label: 'any state' }, { value: 'procured', label: 'procured — never booted' },
  { value: 'inventory', label: 'on the shelf' }, { value: 'deployed', label: 'deployed' },
  { value: 'online', label: 'live now' },
];

function DeviceModal({ verb, eligibleParams, currentDeviceId, onMap, onChanged, onClose, initialMode }) {
  const [mode, setMode] = useState(initialMode || (currentDeviceId && !verb ? 'edit' : (verb ? 'pick' : 'add')));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const PAGE = 30;

  const [flt, setFlt] = useState({ q: '', state: '', owner: '' });
  const [list, setList] = useState({ rows: [], offset: 0, total: null, loading: false });
  const [pick, setPick] = useState('');
  const [owners, setOwners] = useState(null);
  const listRef = React.useRef(null);
  const fetchPage = useCallback(async (reset) => {
    setList((prev) => ({ ...prev, loading: true }));
    try {
      const params = { limit: PAGE, ...(flt.q ? { q: flt.q } : {}), ...(flt.state ? { state: flt.state } : {}),
        ...(flt.owner ? { owner: flt.owner } : {}), ...(eligibleParams || {}) };
      params.offset = reset ? 0 : list.offset + PAGE;
      const d = shapedList((await api.get('/v2/onboarding/screens', { params })).data);
      setList((prev) => ({ rows: reset ? d.rows : [...prev.rows, ...d.rows], offset: params.offset,
        total: Number(d.counts?.total ?? d.rows.length), loading: false }));
    } catch (e) { setNote(e?.response?.data?.error || e.message); setList((prev) => ({ ...prev, loading: false })); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flt, eligibleParams && JSON.stringify(eligibleParams), list.offset]);
  useEffect(() => {
    if (mode !== 'pick') return undefined;
    const t = setTimeout(() => fetchPage(true), flt.q ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, flt]);
  useEffect(() => {
    api.get('/v2/onboarding/device-owners').then((r) => setOwners(r.data?.rows || [])).catch(() => setOwners([]));
  }, []);
  const onListScroll = () => {
    const el = listRef.current;
    if (!el || list.loading || list.total == null || list.rows.length >= list.total) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) fetchPage(false);
  };

  const [add, setAdd] = useState({ imei: '', serial_number: '', widevine_id: '', device_model: '',
    owner_tenant_id: '', device_back_image_key: null, hardware_label_image_key: null });
  const [edit, setEdit] = useState(null);
  const [tenants, setTenants] = useState(null);
  useEffect(() => {
    api.get('/v2/tenants').then((r) => setTenants((r.data?.tenants || []).filter((t) => t.is_active !== false)))
      .catch(() => setTenants([]));
  }, []);
  useEffect(() => {
    if (mode === 'edit' && currentDeviceId && edit == null) {
      api.get('/v2/onboarding/screens', { params: { id: currentDeviceId, limit: 1 } })
        .then((r) => { const c = shapedList(r.data).rows[0]; if (c) setEdit({
          device_model: c.device_model || '', imei: c.device_imei_number || '',
          serial_number: c.device_serial_number || '', widevine_id: c.device_widevine_id || '',
          lock_pin_code: c.lock_pin_code || '',
          device_back_image_key: null, hardware_label_image_key: null, _was: c }); });
    }
  }, [mode, currentDeviceId, edit]);

  const doMap = async (deviceId) => {
    setBusy(true); setNote(null);
    try { await onMap(deviceId); onClose(); }
    catch (e) { setNote(e?.response?.data?.error || e.message); setBusy(false); }
  };
  const confirmAdd = () => {
    const owner = (tenants || []).find((t) => t.id === add.owner_tenant_id)?.name;
    setConfirm({
      title: verb ? `Register & ${verb.toLowerCase()}?` : 'Register this device?',
      label: verb ? `Register & ${verb.toLowerCase()}` : 'Register device',
      body: <>
        <p style={{ margin: '0 0 8px' }}>Registers a <b>procured</b> device ({[add.imei && `IMEI ${add.imei}`, add.serial_number && `SN ${add.serial_number}`].filter(Boolean).join(' · ') || 'identifiers pending'})
          {owner ? <> owned by <b>{owner}</b> from day one (forward-only money-plane transfer)</> : ' as house stock'}
          {verb ? <>, then <b>{verb.toLowerCase()}</b> — its own first boot completes the pairing.</> : '. Its own first boot claims this row — same id, ownership intact.'}</p>
      </>,
      onYes: async () => {
        setBusy(true); setNote(null);
        try {
          const created = (await api.post('/v2/onboarding/devices', {
            imei: add.imei || null, serial_number: add.serial_number || null,
            widevine_id: add.widevine_id || null, device_model: add.device_model || null,
          })).data;
          if (add.device_back_image_key || add.hardware_label_image_key) {
            await api.post(`/v2/onboarding/devices/${created.id}/edit`, {
              device_back_image_key: add.device_back_image_key, hardware_label_image_key: add.hardware_label_image_key,
            });
          }
          if (add.owner_tenant_id) {
            await api.post('/revenue/admin/ownership/transfer', {
              subject_type: 'device', subject_id: created.id, owner_tenant_id: add.owner_tenant_id,
            });
          }
          if (verb && onMap) { await onMap(created.id); } else { onChanged(); }
          onClose();
        } catch (e) { setNote(e?.response?.data?.error || e.message); setBusy(false); setConfirm(null); }
      },
    });
  };
  const confirmEdit = () => {
    const w = edit._was;
    const procured = w.registration_status === 'procured';
    const bits = [];
    if (edit.device_model !== (w.device_model || '')) bits.push(<p key="m" style={{ margin: '0 0 8px' }}>Model → <b>{edit.device_model || 'unknown'}</b>.</p>);
    const idChanged = procured && (edit.imei !== (w.device_imei_number || '') || edit.serial_number !== (w.device_serial_number || '') || edit.widevine_id !== (w.device_widevine_id || ''));
    if (idChanged) bits.push(<p key="i" style={{ margin: '0 0 8px' }}><b>Hardware identifiers change.</b> They are how
      the first boot finds and claims this row — correct intake typos only. If a premise already carries the OLD
      identifiers, re-install from the premise panel so the copies move together.</p>);
    if (edit.lock_pin_code !== (w.lock_pin_code || '')) bits.push(<p key="p" style={{ margin: '0 0 8px' }}>Lock PIN {edit.lock_pin_code ? <>→ <b>{edit.lock_pin_code}</b></> : <b>cleared</b>} — synced onto the premise copy classic reads.</p>);
    if (edit.device_back_image_key) bits.push(<p key="b" style={{ margin: '0 0 8px' }}>Tablet-back photo replaced.</p>);
    if (edit.hardware_label_image_key) bits.push(<p key="l" style={{ margin: '0 0 8px' }}>Serial-label photo replaced.</p>);
    if (!bits.length) { setNote('Nothing changed.'); return; }
    bits.push(<p key="a" style={{ margin: 0 }}>Every changed field is logged — old value, new value, your name.</p>);
    setConfirm({
      title: `Save changes to ${w.thing_name}?`, label: 'Save changes', body: <>{bits}</>,
      onYes: async () => {
        setBusy(true); setNote(null);
        try {
          const body = { device_model: edit.device_model };
          if (edit.lock_pin_code !== (w.lock_pin_code || '')) body.lock_pin_code = edit.lock_pin_code;
          if (procured) { body.imei = edit.imei; body.serial_number = edit.serial_number; body.widevine_id = edit.widevine_id; }
          if (edit.device_back_image_key) body.device_back_image_key = edit.device_back_image_key;
          if (edit.hardware_label_image_key) body.hardware_label_image_key = edit.hardware_label_image_key;
          await api.post(`/v2/onboarding/devices/${currentDeviceId}/edit`, body);
          onChanged(); onClose();
        } catch (e) { setNote(e?.response?.data?.error || e.message); setBusy(false); setConfirm(null); }
      },
    });
  };

  const devPill = (n) => (n.registration_status === 'procured'
    ? <StatusPill tone="warn">procured</StatusPill>
    : rideLive(n) ? <StatusPill tone="grow">riding {n.deployed_at}</StatusPill>
      : <StatusPill tone="dim">shelf</StatusPill>);
  const procuredEdit = edit && edit._was.registration_status === 'procured';

  return (
    <FormModal title="Device" onClose={onClose}>
      {confirm && <ConfirmModal title={confirm.title} body={confirm.body} confirmLabel={confirm.label}
                                onCancel={() => setConfirm(null)} onConfirm={confirm.onYes} />}
      <div className={s.segCtl} role="tablist">
        {verb && <button type="button" role="tab" aria-selected={mode === 'pick'}
                className={`${s.segBtn} ${mode === 'pick' ? s.segOn : ''}`} onClick={() => setMode('pick')}>Pick existing</button>}
        <button type="button" role="tab" aria-selected={mode === 'add'}
                className={`${s.segBtn} ${mode === 'add' ? s.segOn : ''}`} onClick={() => setMode('add')}>Add new</button>
        <button type="button" role="tab" aria-selected={mode === 'edit'} disabled={!currentDeviceId}
                title={currentDeviceId ? undefined : 'No device in context'}
                className={`${s.segBtn} ${mode === 'edit' ? s.segOn : ''}`} onClick={() => currentDeviceId && setMode('edit')}>Edit current</button>
      </div>

      {mode === 'pick' && (
        <>
          <div className={s.modalForm}>
            <span className={s.f}><label>Search</label>
              <input value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} placeholder="thing name or premise…" /></span>
            <div className={s.twoCol}>
              <span className={s.f}><label>State</label>
                <Listbox value={flt.state} onChange={(v) => setFlt({ ...flt, state: v })} options={DEV_STATE_OPTS} ariaLabel="Filter by state" /></span>
              <span className={s.f}><label>Device owner</label>
                <Listbox value={flt.owner} onChange={(v) => setFlt({ ...flt, owner: v })}
                         options={[{ value: '', label: 'any owner' }, ...((owners || []).map((o) => ({ value: o.name, label: o.name })))]}
                         ariaLabel="Filter by device owner" /></span>
            </div>
          </div>
          <div className={s.pickList} ref={listRef} onScroll={onListScroll}>
            {list.rows.map((n) => (
              <button key={n.id} type="button" className={`${s.pickRow} ${pick === n.id ? s.pickOn : ''}`}
                      onClick={() => setPick(n.id)}>
                <span className={s.radio} aria-hidden="true" />
                <span className={s.pickName}>
                  {n.thing_name}
                  <small>{n.device_model || 'model unknown'}{n.imei_tail ? ` · imei …${n.imei_tail}` : ''}{n.owner_name ? ` · ${n.owner_name}` : ' · house'}</small>
                </span>
                {devPill(n)}
              </button>
            ))}
            {list.loading && <div style={{ padding: 12 }}><Loading what="devices" /></div>}
            {!list.loading && !list.rows.length && <div style={{ padding: 14 }} className={s.hint}>No eligible device matches these filters.</div>}
          </div>
          <div className={s.pickFoot}>
            {list.total != null ? `${list.rows.length} of ${list.total} eligible devices loaded — scroll for more` : ' '}
          </div>
          {note && <div className={s.badBanner}>{note}</div>}
          <div className={s.modalFoot}>
            <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
            <button type="button" className={s.btnCreate} disabled={!pick || busy} onClick={() => doMap(pick)}>{verb}</button>
          </div>
        </>
      )}

      {mode === 'add' && (
        <>
          <div className={s.modalForm}>
            <div className={s.twoCol}>
              <span className={s.f}><label>IMEI</label>
                <input value={add.imei} onChange={(e) => setAdd({ ...add, imei: e.target.value })} placeholder="86…" /></span>
              <span className={s.f}><label>Serial number</label>
                <input value={add.serial_number} onChange={(e) => setAdd({ ...add, serial_number: e.target.value })} /></span>
            </div>
            <div className={s.twoCol}>
              <span className={s.f}><label>Widevine id</label>
                <input value={add.widevine_id} onChange={(e) => setAdd({ ...add, widevine_id: e.target.value })} /></span>
              <span className={s.f}><label>Model</label>
                <input value={add.device_model} onChange={(e) => setAdd({ ...add, device_model: e.target.value })} placeholder="DOMO Slate SL10" /></span>
            </div>
            <span className={s.f}><label>Device owner — earns the device share from day one</label>
              <Listbox value={add.owner_tenant_id} onChange={(v) => setAdd({ ...add, owner_tenant_id: v })}
                       options={[{ value: '', label: 'none — house' }, ...((tenants || []).map((t) => ({ value: t.id, label: t.name, hint: t.is_personal ? 'personal' : undefined })))]}
                       ariaLabel="Device owner" /></span>
            <div className={s.twoCol}>
              <DocField label="Tablet back photo" endpoint="/v2/onboarding/devices/upload-url" kind="back"
                        value={add.device_back_image_key} onKey={(k) => setAdd((p2) => ({ ...p2, device_back_image_key: k }))} />
              <DocField label="Serial label photo" endpoint="/v2/onboarding/devices/upload-url" kind="label"
                        value={add.hardware_label_image_key} onKey={(k) => setAdd((p2) => ({ ...p2, hardware_label_image_key: k }))} />
            </div>
            <span className={s.hint}>at least one identifier — it is how the first boot claims this row</span>
          </div>
          {note && <div className={s.badBanner}>{note}</div>}
          <div className={s.modalFoot}>
            <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
            <button type="button" className={s.btnCreate}
                    disabled={busy || (!add.imei.trim() && !add.serial_number.trim() && !add.widevine_id.trim())}
                    onClick={confirmAdd}>
              <Plus size={14} aria-hidden="true" /> {verb ? `Register & ${verb.toLowerCase()}` : 'Register device'}
            </button>
          </div>
        </>
      )}

      {mode === 'edit' && (
        edit == null ? <Loading what="the device" /> : (
          <>
            <div className={s.modalForm}>
              <span className={s.f}><label>Model</label>
                <input value={edit.device_model} onChange={(e) => setEdit({ ...edit, device_model: e.target.value })} /></span>
              <div className={s.twoCol}>
                <span className={s.f}><label>IMEI {procuredEdit ? '' : '(locked after first boot)'}</label>
                  <input value={edit.imei} disabled={!procuredEdit} onChange={(e) => setEdit({ ...edit, imei: e.target.value })} /></span>
                <span className={s.f}><label>Serial number {procuredEdit ? '' : '(locked)'}</label>
                  <input value={edit.serial_number} disabled={!procuredEdit} onChange={(e) => setEdit({ ...edit, serial_number: e.target.value })} /></span>
              </div>
              <span className={s.f}><label>Widevine id {procuredEdit ? '' : '(locked)'}</label>
                <input value={edit.widevine_id} disabled={!procuredEdit} onChange={(e) => setEdit({ ...edit, widevine_id: e.target.value })} /></span>
              <span className={s.f}><label>Lock PIN (3–6 digits — travels with the tablet)</label>
                <input value={edit.lock_pin_code} inputMode="numeric" placeholder="not set"
                       onChange={(e) => setEdit({ ...edit, lock_pin_code: e.target.value.replace(/\D/g, '').slice(0, 6) })} /></span>
              <div className={s.twoCol}>
                <DocField label="Tablet back photo" endpoint="/v2/onboarding/devices/upload-url" kind="back"
                          value={edit.device_back_image_key} existingKey={edit._was.device_back_image_key}
                          onKey={(k) => setEdit((p2) => ({ ...p2, device_back_image_key: k }))} />
                <DocField label="Serial label photo" endpoint="/v2/onboarding/devices/upload-url" kind="label"
                          value={edit.hardware_label_image_key} existingKey={edit._was.hardware_label_image_key}
                          onKey={(k) => setEdit((p2) => ({ ...p2, hardware_label_image_key: k }))} />
              </div>
            </div>
            <p className={s.hint} style={{ margin: 0 }}>{procuredEdit
              ? 'Identifiers are editable while procured — correct intake typos before first boot claims the row.'
              : 'Identifiers are facts reported by the hardware at boot; only model and photos can change.'}
              {' '}Every changed field is logged.</p>
            {note && <div className={s.badBanner}>{note}</div>}
            <div className={s.modalFoot}>
              <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
              <button type="button" className={s.btnCreate} disabled={busy} onClick={confirmEdit}>Save changes</button>
            </div>
          </>
        )
      )}
    </FormModal>
  );
}

function DeviceDrawer({ id, onChanged, onClose, openDrawer }) {
  const { row, err, busy, note, act, fetchRow } = useEntity('/v2/onboarding/screens', id, onChanged);
  const [ownerOpen, setOwnerOpen] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [pmOpen, setPmOpen] = useState(null); // 'pick' | 'edit'
  const [pendingLc, setPendingLc] = useState(null);
  if (!row) return <Drawer onClose={onClose} eyebrow="Device" title="…"><EntityLoading err={err} retry={fetchRow} /></Drawer>;
  const procured = row.registration_status === 'procured';
  const riding = rideLive(row);
  const lc = row.device_lifecycle; // null until V343 is deployed
  const outOfService = OUT_OF_SERVICE.includes(lc);
  return (
    <EntityDrawer eyebrow="Device" title={row.thing_name} onClose={onClose}
      footerNote="A device is workable before its first boot — ownership and installs keep the same id."
      pills={<>
        {procured
          ? <StatusPill tone="warn">procured — never booted</StatusPill>
          : <StatusPill tone={row.last_heard_at && (Date.now() - new Date(row.last_heard_at)) / 60000 <= 10 ? 'grow' : 'dim'}>heard {relTime(row.last_heard_at)}</StatusPill>}
        {row.is_test_device && <StatusPill tone="dim">test device</StatusPill>}
        {outOfService && <StatusPill tone="bad">{lc} — serving off</StatusPill>}
        {riding ? <StatusPill tone="grow">riding {row.premise_plate || row.deployed_at}</StatusPill> : <StatusPill tone="dim">on the shelf</StatusPill>}
      </>}
      sections={[
        { key: 'identity', Icon: Monitor, label: 'device', state: 'ok', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Identity"
              footer={<button type="button" className={s.btnTool} onClick={() => setDmOpen('edit')}>Edit device…</button>}>
              <RvRow label="Name" hint={procured ? 'Placeholder until first boot claims this row with the real thing name.' : 'The IoT thing name, minted at first boot.'}>
                {row.thing_name}
                <div><IdLine id={row.id} /></div>
              </RvRow>
              <RvRow label="Model">{row.device_model || <small>unknown</small>}</RvRow>
              <RvRow label="IMEI"><span className={s.rvMono}>{row.device_imei_number || '—'}</span></RvRow>
              <RvRow label="Serial number"><span className={s.rvMono}>{row.device_serial_number || '—'}</span></RvRow>
              <RvRow label="Widevine id"><span className={s.rvMono}>{row.device_widevine_id || '—'}</span></RvRow>
              <RvRow label="Lock PIN" hint="Unlocks the tablet, so it belongs to the device: it travels on installs and moves, and stays synced with the classic console.">
                {row.lock_pin_code ? <span className={s.rvMono}>{row.lock_pin_code}</span> : <small>not set</small>}
              </RvRow>
              <PhotoRow label="Tablet back photo" docKey={row.device_back_image_key} />
              <PhotoRow label="Serial label photo" docKey={row.hardware_label_image_key}
                        hint="The photographed label is the ground truth the typed identifiers are checked against." />
              <RvRow label="Liveness">
                {procured
                  ? <small>awaiting first boot — no exchanges yet</small>
                  : <><LiveDot status={row.connection_status} at={row.last_heard_at} />heard {relTime(row.last_heard_at)} <small>· flag: {row.connection_status || 'unknown'}</small></>}
              </RvRow>
            </RvCard>
          </>
        ) },
        { key: 'premise', Icon: Boxes, label: 'premise', state: riding ? 'ok' : 'miss', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Premise"
              footer={riding ? (
                <>
                  <button type="button" className={s.btnTool} disabled={busy}
                          onClick={() => act(`/v2/onboarding/premises/${row.deployment_id}/remove-device`, {})}>
                    Remove from premise (back to shelf)
                  </button>
                  <span className={s.spacer} />
                  <button type="button" className={s.btnTool}
                          onClick={() => openDrawer({ kind: 'deployment', id: row.deployment_id })}>
                    Open premise →
                  </button>
                  <button type="button" className={s.btnTool} onClick={() => setPmOpen('edit')}>Edit premise…</button>
                </>
              ) : <button type="button" className={s.btnCreate} onClick={() => setPmOpen('pick')}>Choose premise…</button>}>
              {riding
                ? <RvRow label="Riding" hint="Resolved through the hardware bridge; plays freeze the pairing per batch.">
                    {row.premise_plate || row.deployed_at}
                    {row.premise_plate && row.premise_plate !== row.deployed_at ? <small> · premise {row.deployed_at}</small> : null}
                  </RvRow>
                : <RvRow label={row.deployment_id ? `Last rode ${row.premise_plate || row.deployed_at} (retired)` : 'On the shelf'}
                         hint="Installing copies this device's identifiers onto the premise, clears any previous claim, and logs the move — first boot is NOT required first.">
                    <small>{procured ? 'Fully mappable before first boot.' : 'Available for redeployment.'}</small>
                  </RvRow>}
            </RvCard>
          </>
        ) },
        { key: 'owner', Icon: Building2, label: 'owner', state: row.owner_name ? 'ok' : 'miss', render: () => (
          <RvCard title="Ownership"
            footer={<button type="button" className={s.btnTool} onClick={() => setOwnerOpen(true)}>
              {row.owner_name ? 'Transfer ownership' : 'Set owner'}
            </button>}>
            <RvRow label="Device owner" hint="Earns the device share of this device's plays; forward-only transfers — past earnings keep their past owner.">
              {row.owner_name || <small>none — house money</small>}
            </RvRow>
          </RvCard>
        ) },
        { key: 'lifecycle', Icon: Power, label: 'lifecycle', state: lc == null || lc === 'active' || lc === 'procured' ? 'ok' : 'miss', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Lifecycle"
              footer={lc != null && <>
                {(procured ? ['retired', 'lost'] : ['active', 'retired', 'lost', 'rma']).map((to) => (
                  <button key={to} type="button" className={to === 'active' ? s.btnCreate : s.btnTool}
                          disabled={busy || to === lc} onClick={() => setPendingLc(to)}>
                    {to === 'active' ? 'Mark active' : `Mark ${to}`}
                  </button>
                ))}
              </>}>
              <RvRow label="Status" hint={lc == null
                ? 'Device lifecycle arrives with migration V343 — deploy pending.'
                : 'The ops truth about this unit. Serving eligibility (is_active) derives from it: only ACTIVE serves; retirement is serve-never-bill — books stay play-true.'}>
                {lc == null ? <small>not available yet</small>
                  : <StatusPill tone={lc === 'active' ? 'grow' : lc === 'procured' ? 'warn' : 'bad'}>{lc}</StatusPill>}
              </RvRow>
              {procured && lc != null && (
                <RvRow label="Never booted"><small>Its own first boot activates it — mark it retired or lost only if the purchase fell through.</small></RvRow>
              )}
            </RvCard>
          </>
        ) },
        { key: 'log', Icon: History, label: 'action log', render: () => (
          <RvCard title="Action log">
            <div className={s.rvRow}>
              <ActivityLog params={{ device_ids: row.id }} />
            </div>
          </RvCard>
        ) },
      ]}>
      {pendingLc && (
        <ConfirmModal title={`Mark ${row.thing_name} ${pendingLc}?`}
          confirmLabel={`Mark ${pendingLc}`} danger={pendingLc !== 'active'}
          body={<>
            <p style={{ margin: '0 0 8px' }}>{DEVICE_LIFECYCLE_WORDS[pendingLc]}</p>
            <p style={{ margin: 0 }}>The change is logged — who, when, from what, to what.</p>
          </>}
          onConfirm={async () => { await act(`/v2/onboarding/devices/${row.id}/lifecycle`, { status: pendingLc }); setPendingLc(null); }}
          onCancel={() => setPendingLc(null)} />
      )}
      {ownerOpen && <SetOwnerModal device={row} onDone={() => { fetchRow(); onChanged(); }} onClose={() => setOwnerOpen(false)} />}
      {dmOpen && <DeviceModal verb={null} eligibleParams={null} currentDeviceId={row.id} initialMode="edit"
        onMap={null} onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setDmOpen(null)} />}
      {pmOpen && <PremiseModal initialMode={pmOpen} verb="Install here" eligibleParams={{ missing: 'screen' }}
        currentPremiseId={rideLive(row) ? row.deployment_id : null}
        onMap={async (pid) => { await api.post(`/v2/onboarding/premises/${pid}/install-device`, { device_id: row.id }); await fetchRow(); onChanged(); }}
        onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setPmOpen(null)} />}
    </EntityDrawer>
  );
}

function SimDrawer({ id, onChanged, onClose, openDrawer }) {
  const { row, err, busy, note, act, fetchRow } = useEntity('/v2/onboarding/sims', id, onChanged);
  const [pmOpen, setPmOpen] = useState(null); // 'pick' | 'edit'
  const [editOpen, setEditOpen] = useState(false);
  if (!row) return <Drawer onClose={onClose} eyebrow="SIM" title="…"><EntityLoading err={err} retry={fetchRow} /></Drawer>;
  const allocated = !!row.deployment_id;
  return (
    <EntityDrawer eyebrow="SIM" title={row.sim_number} onClose={onClose}
      footerNote="Allocations are dated — history accumulates, nothing is overwritten."
      pills={<>
        {allocated ? <StatusPill tone="grow">in {row.allocated_to}</StatusPill> : <StatusPill tone="warn">in stock</StatusPill>}
        <span className={s.hint}>{row.provider || 'provider unknown'} · {inrP(row.monthly_cost_paise)}/month</span>
      </>}
      sections={[
        { key: 'identity', Icon: CreditCard, label: 'SIM', state: 'ok', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Identity"
              footer={<button type="button" className={s.btnTool} onClick={() => setEditOpen(true)}>Edit SIM…</button>}>
              <RvRow label="ICCID">{row.sim_number}<div><IdLine id={row.id} /></div></RvRow>
              <RvRow label="Provider">{row.provider || <small>unknown</small>}</RvRow>
              <RvRow label="Mobile number"><span className={s.rvMono}>{row.mobile_number || '—'}</span></RvRow>
              <RvRow label="Plan">{row.plan_name || <small>not recorded</small>}</RvRow>
              <RvRow label="Monthly cost" hint="Recovers from the premise owner at payout close, before TDS — ₹170 is the default, not a fact.">
                {inrP(row.monthly_cost_paise)}/month
              </RvRow>
              <RvRow label="Status">{row.status}</RvRow>
              <PhotoRow label="SIM card photo" docKey={row.card_image_key} />
              <PhotoRow label="Envelope photo (PIN/PUK)" docKey={row.envelope_image_key} />
            </RvCard>
          </>
        ) },
        { key: 'premise', Icon: Boxes, label: 'premise', state: allocated ? 'ok' : 'miss', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Premise"
              footer={allocated ? (
                <>
                  <button type="button" className={s.btnTool} disabled={busy}
                          onClick={() => act(`/v2/onboarding/sims/${row.id}/deallocate`, {})}>
                    Return to stock
                  </button>
                  <span className={s.spacer} />
                  <button type="button" className={s.btnTool}
                          onClick={() => openDrawer({ kind: 'deployment', id: row.deployment_id })}>
                    Open premise →
                  </button>
                  <button type="button" className={s.btnTool} onClick={() => setPmOpen('edit')}>Edit premise…</button>
                </>
              ) : <button type="button" className={s.btnCreate} onClick={() => setPmOpen('pick')}>Choose premise…</button>}>
              {allocated
                ? <RvRow label="Serving" hint={`Since ${fmtDate(row.allocated_since)}. Its cost recovers from ${row.recovers_from || 'the premise owner'}.`}>
                    {row.allocated_to}
                  </RvRow>
                : <RvRow label="In stock" hint="Allocate from here or from the premise's panel — the same dated binding either way.">
                    <small>Not serving any premise yet.</small>
                  </RvRow>}
            </RvCard>
          </>
        ) },
        { key: 'log', Icon: History, label: 'action log', render: () => (
          <RvCard title="Action log">
            <div className={s.rvRow}>
              {row.deployment_id
                ? <ActivityLog params={{ sim_ids: row.id }} />
                : <p className={s.hint}>No premise events yet — allocations will appear here once they happen.</p>}
            </div>
          </RvCard>
        ) },
      ]}>
      {editOpen && <EntityEditModal title={`SIM ${row.sim_number}`} endpoint={`/v2/onboarding/sims/${row.id}/edit`}
        fields={SIM_EDIT_FIELDS}
        photoFields={[
          { key: 'card_image_key', label: 'SIM card photo', endpoint: '/v2/onboarding/sims/upload-url', kind: 'card', existingKey: row.card_image_key },
          { key: 'envelope_image_key', label: 'Envelope photo (PIN/PUK)', endpoint: '/v2/onboarding/sims/upload-url', kind: 'envelope', existingKey: row.envelope_image_key },
        ]}
        initial={{ sim_number: row.sim_number, provider: row.provider, mobile_number: row.mobile_number,
          imsi_number: row.imsi_number, plan_name: row.plan_name, monthly_cost_paise: row.monthly_cost_paise, notes: row.notes }}
        onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setEditOpen(false)} />}
      {pmOpen && <PremiseModal initialMode={pmOpen} verb="Allocate here" eligibleParams={{ missing: 'sim' }}
        currentPremiseId={row.deployment_id || null}
        onMap={async (pid) => { await api.post(`/v2/onboarding/sims/${row.id}/allocate`, { edge_node_id: pid }); await fetchRow(); onChanged(); }}
        onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setPmOpen(null)} />}
    </EntityDrawer>
  );
}

function VehicleDrawer({ id, onChanged, onClose, openDrawer }) {
  const { row, err, busy, note, act, fetchRow } = useEntity('/v2/onboarding/vehicles', id, onChanged);
  const [pmOpen, setPmOpen] = useState(null); // 'pick' | 'edit'
  const [editOpen, setEditOpen] = useState(false);
  if (!row) return <Drawer onClose={onClose} eyebrow="Vehicle" title="…"><EntityLoading err={err} retry={fetchRow} /></Drawer>;
  const linked = !!row.deployment_id;
  const d = daysTo(row.nearest_doc_expiry);
  return (
    <EntityDrawer eyebrow="Vehicle" title={row.registration_number} onClose={onClose}
      footerNote="Document images and expiries are managed in classic Drivers & vehicles."
      pills={<>
        {linked ? <StatusPill tone="grow">carries {row.linked_to}</StatusPill> : <StatusPill tone="warn">unlinked</StatusPill>}
        {row.nearest_doc_expiry && d != null && d <= 30 && <StatusPill tone="bad">docs expiring {fmtDate(row.nearest_doc_expiry)}</StatusPill>}
      </>}
      sections={[
        { key: 'identity', Icon: Car, label: 'vehicle', state: 'ok', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Identity"
              footer={<button type="button" className={s.btnTool} onClick={() => setEditOpen(true)}>Edit vehicle…</button>}>
              <RvRow label="Registration">{row.registration_number}<div><IdLine id={row.id} /></div></RvRow>
              <RvRow label="Make">{[row.make, row.model, row.model_year].filter(Boolean).join(' ') || <small>unknown</small>}</RvRow>
              <RvRow label="Chassis number"><span className={s.rvMono}>{row.chassis_number || '—'}</span></RvRow>
              <RvRow label="Body type">{row.body_type || <small>not recorded</small>}</RvRow>
              <RvRow label="RC registered on" hint="The RC's date of registration — distinct from its expiry below.">
                {row.rc_registered_on ? fmtDate(row.rc_registered_on) : <small>not recorded</small>}
              </RvRow>
              <RvRow label="Nearest document expiry" hint="Across RC · insurance · PUC · fitness · permit.">
                {row.nearest_doc_expiry ? fmtDate(row.nearest_doc_expiry) : <small>none recorded</small>}
              </RvRow>
            </RvCard>
          </>
        ) },
        { key: 'premise', Icon: Boxes, label: 'premise', state: linked ? 'ok' : 'miss', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Premise"
              footer={linked ? (
                <>
                  <button type="button" className={s.btnTool} disabled={busy}
                          onClick={() => act(`/v2/onboarding/premises/${row.deployment_id}/vehicle`, { vehicle_id: null })}>
                    Unlink
                  </button>
                  <span className={s.spacer} />
                  <button type="button" className={s.btnTool}
                          onClick={() => openDrawer({ kind: 'deployment', id: row.deployment_id })}>
                    Open premise →
                  </button>
                  <button type="button" className={s.btnTool} onClick={() => setPmOpen('edit')}>Edit premise…</button>
                </>
              ) : <button type="button" className={s.btnCreate} onClick={() => setPmOpen('pick')}>Choose premise…</button>}>
              {linked
                ? <RvRow label="Carries" hint="The dated device_vehicles binding and the V334 FK move together.">{row.linked_to}</RvRow>
                : <RvRow label="Unlinked" hint="Link from here or from the premise's panel — the same audited write either way.">
                    <small>Not carrying any premise yet.</small>
                  </RvRow>}
            </RvCard>
          </>
        ) },
        { key: 'driver', Icon: Users, label: 'driver', state: row.driver_name ? 'ok' : (linked ? 'miss' : 'na'), render: () => (
          <RvCard title="Driver">
            {row.driver_name
              ? <RvRow label="Active driver" hint="Assigned to the premise this vehicle carries.">{row.driver_name}</RvRow>
              : <RvRow label="No active driver" hint={linked ? 'Assign one from the Drivers tab or the premise panel.' : 'Link the vehicle to a premise first.'}>
                  <small>Advisory for money — the gate runs on trips and hours.</small>
                </RvRow>}
          </RvCard>
        ) },
        { key: 'log', Icon: History, label: 'action log', render: () => (
          <RvCard title="Action log">
            <div className={s.rvRow}>
              {row.deployment_id
                ? <ActivityLog params={{ vehicle_ids: row.id }} />
                : <p className={s.hint}>No premise events yet.</p>}
            </div>
          </RvCard>
        ) },
      ]}>
      {editOpen && <EntityEditModal title={`Vehicle ${row.registration_number}`} endpoint={`/v2/onboarding/vehicles/${row.id}/edit`}
        fields={VEHICLE_EDIT_FIELDS}
        initial={{ registration_number: row.registration_number, make: row.make, model: row.model, model_year: row.model_year,
            colour: row.colour, fuel_type: row.fuel_type,
            chassis_number: row.chassis_number, body_type: row.body_type,
            rc_registered_on: row.rc_registered_on, notes: row.notes,
            rc_expires_on: row.rc_expires_on, insurance_expires_on: row.insurance_expires_on,
            puc_expires_on: row.puc_expires_on, fitness_expires_on: row.fitness_expires_on,
            permit_expires_on: row.permit_expires_on }}
        onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setEditOpen(false)} />}
      {pmOpen && <PremiseModal initialMode={pmOpen} verb="Link here" eligibleParams={{ type: 'in_cab', missing: 'vehicle' }}
        currentPremiseId={row.deployment_id || null}
        onMap={async (pid) => { await api.post(`/v2/onboarding/premises/${pid}/vehicle`, { vehicle_id: row.id }); await fetchRow(); onChanged(); }}
        onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setPmOpen(null)} />}
    </EntityDrawer>
  );
}

/* ── driver KYC documents — six rows, presign→PUT→commit-key-via-edit (the
   media console is the OTHER writer of this namespace; keys are always
   fresh, the person edit commits them audited). Viewing is its own request:
   the server mints a short-lived URL and logs the disclosure. ─────────── */

const DRIVER_DOC_ROWS = [
  { kind: 'photo', col: 'image_key', label: 'Photo' },
  { kind: 'aadhaar', col: 'aadhaar_image_key', label: 'Aadhaar' },
  { kind: 'pan', col: 'pan_image_key', label: 'PAN card' },
  { kind: 'licence', col: 'license_image_key', label: 'Driving licence' },
  { kind: 'address_proof', col: 'address_proof_image_key', label: 'Address proof' },
  { kind: 'permit', col: 'permit_image_key', label: 'Municipal permit' },
];

function DriverDocRow({ driverId, kind, col, label, onRecord, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // the same file can be re-picked after a failure
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const ct = file.type || 'image/jpeg';
      const pre = (await api.post(`/v2/onboarding/driver-persons/${driverId}/upload-url`, { kind, contentType: ct })).data;
      const put = await fetch(pre.uploadUrl, { method: 'PUT', headers: { 'Content-Type': ct }, body: file });
      if (!put.ok) throw new Error(`upload failed (${put.status})`);
      await api.post(`/v2/onboarding/driver-persons/${driverId}/edit`, { [col]: pre.key });
      onChanged();
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };
  const view = async () => {
    setErr(null);
    try {
      const r = await api.get(`/v2/onboarding/driver-persons/${driverId}/documents/${kind}`);
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message);
    }
  };
  return (
    <RvRow label={label}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {onRecord ? <StatusPill tone="grow">on record</StatusPill> : <StatusPill tone="dim">missing</StatusPill>}
        {onRecord && <button type="button" className={s.btnTool} disabled={busy} onClick={view}>View</button>}
        <label className={s.btnTool}>
          {busy ? 'uploading…' : (onRecord ? 'Replace…' : 'Upload…')}
          <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
                 onChange={pick} disabled={busy} aria-label={`Upload ${label}`} />
        </label>
      </span>
      {err && <div className={s.rvHint} style={{ color: 'var(--bad-text)' }}>{err}</div>}
    </RvRow>
  );
}

function DriverDrawer({ id, onChanged, onClose, openDrawer }) {
  const { row, err, busy, note, act, fetchRow } = useEntity('/v2/onboarding/drivers', id, onChanged);
  const [pmOpen, setPmOpen] = useState(null); // 'pick' | 'edit'
  const [editOpen, setEditOpen] = useState(false);
  const [personOpen, setPersonOpen] = useState(false);
  const [person, setPerson] = useState(null); // V346 person facts, fetched on demand
  useEffect(() => {
    if (!row?.driver_id) return undefined;
    let live = true;
    api.get('/v2/onboarding/driver-persons/' + row.driver_id)
      .then((r) => { if (live) setPerson(r.data.person || null); })
      .catch(() => { if (live) setPerson(null); });
    return () => { live = false; };
  }, [row?.driver_id]);
  const reloadPerson = useCallback(() => {
    if (!row?.driver_id) return;
    api.get('/v2/onboarding/driver-persons/' + row.driver_id)
      .then((r) => setPerson(r.data.person || null)).catch(() => {});
  }, [row?.driver_id]);
  if (!row) return <Drawer onClose={onClose} eyebrow="Driver" title="…"><EntityLoading err={err} retry={fetchRow} /></Drawer>;
  const active = row.is_active;
  return (
    <EntityDrawer eyebrow="Driver" title={row.driver_name} onClose={onClose}
      footerNote="Assignments are dated intervals; KYC documents live on the person, in the driver pane."
      pills={<>
        {active
          ? <StatusPill tone="grow">drives {row.cab_plate || row.assigned_to}</StatusPill>
          : <StatusPill tone="dim">past assignment</StatusPill>}
        {row.driver_mobile && <span className={s.hint}>{row.driver_mobile}</span>}
      </>}
      sections={[
        { key: 'identity', Icon: Users, label: 'driver', state: 'ok', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Identity"
              footer={<>
                <button type="button" className={s.btnTool} onClick={() => setEditOpen(true)}>Edit driver…</button>
                {row.driver_id && <button type="button" className={s.btnTool} onClick={() => setPersonOpen(true)}>Edit person…</button>}
                {row.driver_id && person && (
                  <button type="button" className={s.btnTool} disabled={busy}
                          onClick={() => act(`/v2/onboarding/driver-persons/${row.driver_id}/${person.archived_at ? 'unarchive' : 'archive'}`, {})
                            .then(() => api.get('/v2/onboarding/driver-persons/' + row.driver_id).then((r) => setPerson(r.data.person || null)))}>
                    {person.archived_at ? 'Unarchive person' : 'Archive person'}
                  </button>
                )}
              </>}>
              <RvRow label="Name">{row.driver_name}
                {person?.archived_at ? <> <StatusPill tone="bad">archived</StatusPill></> : null}
                <div><IdLine id={row.id} /></div></RvRow>
              <RvRow label="Mobile"><span className={s.rvMono}>{row.driver_mobile || '—'}</span></RvRow>
              {person && (
                <>
                  <RvRow label="PAN"><span className={s.rvMono}>{person.pan_number || <small>not recorded</small>}</span></RvRow>
                  <RvRow label="Work city">{person.work_city || <small>not recorded</small>}</RvRow>
                  <RvRow label="Licence / permit expiries">
                    {person.dl_expires_on ? `DL ${fmtDate(person.dl_expires_on)}` : 'DL —'}
                    {' · '}
                    {person.permit_expires_on ? `permit ${fmtDate(person.permit_expires_on)}` : 'permit —'}
                  </RvRow>
                </>
              )}
              <RvRow label="Interval">
                {active ? `since ${fmtDate(row.assigned_at)}` : `${fmtDate(row.assigned_at)} → ${fmtDate(row.unassigned_at)}`}
              </RvRow>
            </RvCard>
            {row.driver_id && person && (
              <RvCard title="Documents">
                {DRIVER_DOC_ROWS.map((d) => (
                  <DriverDocRow key={d.kind} driverId={row.driver_id} kind={d.kind} col={d.col} label={d.label}
                                onRecord={!!person.documents?.[d.kind]} onChanged={reloadPerson} />
                ))}
                <div className={s.rvRow}>
                  <p className={s.hint}>
                    Uploads replace the pointer, never the file — every version stays in the store,
                    and each View mints a short-lived link that is logged as a disclosure.
                  </p>
                </div>
              </RvCard>
            )}
          </>
        ) },
        { key: 'cab', Icon: Boxes, label: 'cab', state: active ? 'ok' : 'miss', render: () => (
          <>
            {note && <div className={s.badBanner}>{note}</div>}
            <RvCard title="Cab"
              footer={<>
                {active && (
                  <button type="button" className={s.btnTool} disabled={busy}
                          onClick={() => act(`/v2/onboarding/drivers/${row.id}/unassign`, {})}>
                    Unassign
                  </button>
                )}
                <span className={s.spacer} />
                {active && <button type="button" className={s.btnTool} onClick={() => setPmOpen(true)}>Edit cab premise…</button>}
                <button type="button" className={s.btnCreate} onClick={() => setPmOpen(true)}>
                  {active ? 'Reassign…' : 'Assign…'}
                </button>
              </>}>
              {active
                ? <RvRow label="Drives" hint="Reassigning closes this interval and opens a new one at the new cab — identity carries over.">
                    {row.cab_plate || row.assigned_to}{row.cab_plate && row.assigned_to !== row.cab_plate ? <small> · premise {row.assigned_to}</small> : null}
                  </RvRow>
                : <RvRow label="Not assigned" hint="Assigning opens a fresh dated interval with this driver's identity.">
                    <small>Advisory for money — the gate runs on trips and hours.</small>
                  </RvRow>}
            </RvCard>
          </>
        ) },
        { key: 'log', Icon: History, label: 'action log', render: () => (
          <RvCard title="Action log">
            <div className={s.rvRow}>
              {row.deployment_id
                ? <ActivityLog params={{ driver_ids: row.id }} />
                : <p className={s.hint}>No premise events for this interval.</p>}
            </div>
          </RvCard>
        ) },
      ]}>
      {personOpen && row.driver_id && <EntityEditModal title={`Person ${row.driver_name}`}
        endpoint={`/v2/onboarding/driver-persons/${row.driver_id}/edit`}
        fields={DRIVER_PERSON_FIELDS}
        initial={{
          pan_number: person?.pan_number || '', work_city: person?.work_city || '',
          permanent_address: person?.permanent_address || '', permanent_pincode: person?.permanent_pincode || '',
          communication_address: person?.communication_address || '', communication_pincode: person?.communication_pincode || '',
          dl_expires_on: person?.dl_expires_on || '', permit_expires_on: person?.permit_expires_on || '',
          notes: person?.notes || '',
        }}
        onChanged={() => { fetchRow(); onChanged(); api.get('/v2/onboarding/driver-persons/' + row.driver_id).then((r) => setPerson(r.data.person || null)); }}
        onClose={() => setPersonOpen(false)} />}
      {editOpen && <EntityEditModal title={`Driver ${row.driver_name}`} endpoint={`/v2/onboarding/drivers/${row.id}/edit`}
        fields={DRIVER_EDIT_FIELDS}
        initial={{ driver_name: row.driver_name, driver_mobile: row.driver_mobile }}
        onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setEditOpen(false)} />}
      {pmOpen && <PremiseModal initialMode={pmOpen} verb={active ? 'Reassign here' : 'Assign here'} eligibleParams={{ type: 'in_cab' }}
        currentPremiseId={row.deployment_id || null}
        onMap={async (pid) => {
          const r = await api.post(`/v2/onboarding/drivers/${row.id}/reassign`, { edge_node_id: pid });
          onChanged();
          if (r.data && r.data.id) openDrawer({ kind: 'driver', id: r.data.id });
        }}
        onChanged={() => { fetchRow(); onChanged(); }} onClose={() => setPmOpen(null)} />}
    </EntityDrawer>
  );
}

function DriverIntake({ onDone, onClose, fixedPremise }) {
  const [f, setF] = useState({ driver_name: '', driver_mobile: '', edge_node_id: fixedPremise?.id || '' });
  const [note, setNote] = useState(null);
  const cabs = usePremises((n) => isCab(n), []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setNote(null);
    try {
      await api.post('/v2/onboarding/drivers', {
        driver_name: f.driver_name, driver_mobile: f.driver_mobile || null, edge_node_id: f.edge_node_id,
      });
      onDone(); onClose();
    } catch (e) {
      setNote({ ok: false, text: e?.response?.data?.error || e.message });
    }
  };
  return (
    <FormModal title="Add driver" onClose={onClose}>
      <p className={s.modalBody} style={{ margin: 0 }}>A driver enters the system by being assigned —
        the interval is dated from now. Documents are added later in classic Drivers &amp; vehicles.</p>
      <div className={s.modalForm}>
        <div className={s.twoCol}>
          <span className={s.f}><label>Name</label>
            <input value={f.driver_name} onChange={set('driver_name')} /></span>
          <span className={s.f}><label>Mobile</label>
            <input value={f.driver_mobile} onChange={set('driver_mobile')} placeholder="+91…" /></span>
        </div>
        {fixedPremise
          ? <span className={s.f}><label>Cab</label>
              <input value={fixedPremise.name} disabled /></span>
          : <span className={s.f}><label>Cab</label>
              <Listbox value={f.edge_node_id} onChange={(v) => setF({ ...f, edge_node_id: v })}
                       options={(cabs || []).map((n) => ({ value: n.id, label: n.node_name }))}
                       placeholder="pick a cab…" ariaLabel="Cab" /></span>}
      </div>
      {note && <div className={s.badBanner}>{note.text}</div>}
      <div className={s.modalFoot}>
        <button type="button" className={s.btnTool} onClick={onClose}>Cancel</button>
        <button type="button" className={s.btnCreate} disabled={!f.driver_name.trim() || !f.edge_node_id} onClick={submit}>
          <Plus size={14} aria-hidden="true" /> Add driver
        </button>
      </div>
    </FormModal>
  );
}

function BatchDrawer({ sel, onClose }) {
  const parts = DIMS.filter((d) => sel[d].length).map((d) => `${sel[d].length} ${DIM_LABEL[d]}`);
  return (
    <Drawer onClose={onClose} eyebrow="Selection" title={parts.join(' · ') || 'Nothing selected'}>
      <div className={s.paneScroll}>
        <RvCard title="Action log — everything the selection touches">
          <div className={s.rvRow}>
            <ActivityLog params={selParams(sel, null)} />
          </div>
        </RvCard>
      </div>
    </Drawer>
  );
}

function Drawer({ title, sub, eyebrow, head, footer, children, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className={s.scrim} onClick={onClose} aria-hidden="true" />
      <aside className={s.drawer} role="dialog" aria-label={typeof title === 'string' ? title : 'Details'}>
        <div className={s.drawerHead}>
          {eyebrow && <span className={s.eyebrow}>{eyebrow}</span>}
          <div className={s.drawerTitleRow}>
            <span className={s.drawerTitle}>{title}</span>
            <button type="button" className={s.drawerClose} onClick={onClose} aria-label="Close">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          {sub && <span className={s.drawerSub}>{sub}</span>}
          {head}
        </div>
        {children}
        {footer && <div className={s.drawerFoot}>{footer}</div>}
      </aside>
    </>
  );
}

/* ── the workspace ─────────────────────────────────────────────────────── */

const TAB_DEFS = [
  { id: 'deployments', label: 'Premises', Icon: Boxes, C: DeploymentsTab, ph: 'Search premises — plate, device, SIM, operator…' },
  { id: 'screens', label: 'Devices', Icon: Monitor, C: ScreensTab, ph: 'Search devices or the premise they ride…' },
  { id: 'sims', label: 'SIMs', Icon: CreditCard, C: SimsTab, ph: 'Search SIMs — ICCID, provider, deployment…' },
  { id: 'vehicles', label: 'Vehicles', Icon: Car, C: VehiclesTab, ph: 'Search vehicles — plate, make, deployment…' },
  { id: 'drivers', label: 'Drivers', Icon: Users, C: DriversTab, ph: 'Search drivers or their cab…' },
  { id: 'premise_owners', label: 'Premise owners', Icon: Building2, C: PremiseOwnersTab, ph: 'Search premise owners…' },
  { id: 'device_owners', label: 'Device owners', Icon: Cpu, C: DeviceOwnersTab, ph: 'Search device owners…' },
];

// The drawer/registry anatomy is exported for sibling domains (Tenants):
// ONE grammar, imported — never re-implemented (the BM-precedent law).
export { EntityDrawer, Drawer, RvCard, RvRow, StatusPill, IdLine, EntityLoading, ExportButton, exportCsv, ConfirmModal };

export default function Onboarding() {
  const [params, setParams] = useSearchParams();
  const tab = TAB_DEFS.some((t) => t.id === params.get('tab')) ? params.get('tab') : 'deployments';
  const [sel, setSel] = useState(EMPTY_SEL);

  const [drawer, setDrawer] = useState(null);
  const [refresh, setRefresh] = useState(0);
  // deep links (e.g. the tenant drawer) may carry ?q=<owner name>
  const [q, setQ] = useState(() => params.get('q') || '');
  const onChanged = () => setRefresh((n) => n + 1);


  const toggle = (dim, id) => setSel((prev) => ({
    ...prev,
    [dim]: prev[dim].includes(id) ? prev[dim].filter((x) => x !== id) : [...prev[dim], id],
  }));
  const toggleAll = (dim, pageIds) => setSel((prev) => {
    const allOn = pageIds.length > 0 && pageIds.every((id) => prev[dim].includes(id));
    return { ...prev, [dim]: allOn ? prev[dim].filter((id) => !pageIds.includes(id)) : [...new Set([...prev[dim], ...pageIds])] };
  });
  const clearDim = (dim) => setSel((prev) => ({ ...prev, [dim]: [] }));

  const active = TAB_DEFS.find((t) => t.id === tab);
  const Active = active.C;

  return (
    <div className={s.wrap}>
      <header style={{ marginBottom: 'var(--s3)', display: 'flex', alignItems: 'flex-end', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ font: '700 20px var(--f-display)', letterSpacing: '-.02em', color: 'var(--ink)', margin: 0 }}>Assets &amp; onboarding</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-soft)', maxWidth: 780 }}>
            Seven registries, one selection. Check rows in any tab and every other tab narrows to what
            shares a deployment with them; click a row to open it.
          </p>
        </div>
        {anySelected(sel) && (
          <span className={s.stripRight}>
            <button type="button" className={s.btnTool} onClick={() => setDrawer({ kind: 'batch' })}>
              <ScrollText size={13} aria-hidden="true" /> Action log for selection
            </button>
            <button type="button" className={s.btnTool} onClick={() => setSel(EMPTY_SEL)}>Clear all</button>
          </span>
        )}
      </header>

      <div className={s.searchBar}>
        <Search size={15} aria-hidden="true" style={{ color: 'var(--ink-mute)', flex: 'none' }} />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
               placeholder={active.ph} aria-label={active.ph} />
      </div>

      <div className={s.tabRow} role="tablist">
        {TAB_DEFS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
                  className={`${s.tabCard} ${tab === t.id ? s.tabCardOn : ''}`}
                  onClick={() => { setQ(''); setParams({ tab: t.id }, { replace: true }); }}>
            <t.Icon size={tab === t.id ? 17 : 15} aria-hidden="true" />
            <span>{t.label}</span>
            {sel[t.id].length > 0 && (
              <span className={s.tabSel}>
                {sel[t.id].length} selected
                <span role="button" tabIndex={0} className={s.tabSelX} title="Clear this selection"
                      onClick={(e) => { e.stopPropagation(); clearDim(t.id); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); clearDim(t.id); } }}>
                  <X size={9} aria-hidden="true" />
                </span>
              </span>
            )}
          </button>
        ))}
      </div>

      <Active sel={sel} toggle={toggle} toggleAll={toggleAll}
              openDrawer={setDrawer} q={q} refresh={refresh} onChanged={onChanged} />

      {drawer?.kind === 'deployment' && (
        <DeploymentDrawer id={drawer.id} section={drawer.section}
                          onChanged={onChanged} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'device' && (
        <DeviceDrawer id={drawer.id} onChanged={onChanged} openDrawer={setDrawer} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'sim' && (
        <SimDrawer id={drawer.id} onChanged={onChanged} openDrawer={setDrawer} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'vehicle' && (
        <VehicleDrawer id={drawer.id} onChanged={onChanged} openDrawer={setDrawer} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'driver' && (
        <DriverDrawer id={drawer.id} onChanged={onChanged} openDrawer={setDrawer} onClose={() => setDrawer(null)} />
      )}
      {drawer?.kind === 'batch' && <BatchDrawer sel={sel} onClose={() => setDrawer(null)} />}
    </div>
  );
}
